import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, Building2, ChevronRight, Clock, FileText, Shield, CheckCircle, ArrowLeft, Info, Save, Play, AlertTriangle, AlertCircle, Check, Download, Share2, Briefcase, Users, Home, FileCheck, Globe, Sparkles, Plus, Loader2, Zap, Timer, Heart, Scale, Plane, FileWarning, HelpCircle, Copy, Archive, RefreshCw } from 'lucide-react';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { DownloadReportModal } from '../components/modals/DownloadReportModal';
import { ShareReportModal } from '../components/modals/ShareReportModal';
type ViewState = 'landing' | 'setup' | 'wizard' | 'results' | 'action-plan' | 'history';
type AssessmentType = 'personal' | 'business';
export interface LegalHealthCheckProps {
  embedded?: boolean;
  initialAssessmentType?: AssessmentType;
  initialViewState?: ViewState;
  onExit?: () => void;
}
// Helper icon component
function Scroll(props: any) {
  return <FileText {...props} data-id="element-712" />;
}
const PERSONAL_SECTIONS = [{
  id: 'family',
  title: 'Family & Relationship Changes',
  icon: Heart,
  whyItMatters: 'Family law varies by province. Formal agreements like Cohabitation or Separation Agreements provide certainty and protect your assets, while documented guardianship wishes ensure your children are cared for by people you choose.',
  commonlyOverlooked: 'Common-law partners do not automatically have the same property rights as married spouses in many provinces.',
  whenToReview: 'After marriage, separation, birth of a child, or when a dependant requires special care.'
}, {
  id: 'estate',
  title: 'Wills, POA & Estate Planning',
  icon: Scroll,
  whyItMatters: 'In Canada, dying without a Will (intestate) means provincial laws dictate how your assets are divided, which may not align with your wishes. A Power of Attorney ensures someone you trust can manage your finances and healthcare if you become incapacitated.',
  commonlyOverlooked: 'Digital assets (crypto, social media, online accounts) are often forgotten in estate planning. Beneficiary designations on RRSPs/TFSAs override your Will.',
  whenToReview: 'Every 3-5 years, or after a major life event like marriage, divorce, or the death of an executor.'
}, {
  id: 'property',
  title: 'Property & Housing',
  icon: Home,
  whyItMatters: 'How you structure property ownership (e.g., Joint Tenancy vs. Tenants in Common) drastically affects what happens to the property when an owner passes away. For renters, a written lease protects your rights under provincial tenancy acts.',
  commonlyOverlooked: 'Title fraud is a growing risk; title insurance can protect you even years after purchasing a home.',
  whenToReview: 'When buying/selling, renewing a mortgage, or changing who lives in the property.'
}, {
  id: 'work_money',
  title: 'Work, Money & Disputes',
  icon: Briefcase,
  whyItMatters: 'A clear employment contract protects your rights regarding severance and termination. Personal liability insurance acts as a crucial safety net against unforeseen lawsuits or claims that could jeopardize your personal assets.',
  commonlyOverlooked: 'Independent contractors may actually be employees under the law, entitling them to severance and benefits.',
  whenToReview: 'Starting a new job, facing workplace conflict, or dealing with significant debt.'
}, {
  id: 'special',
  title: 'International & Special Situations',
  icon: Plane,
  whyItMatters: 'Cross-border assets, immigration status, and international travel with children require specific legal documentation to avoid border delays, tax complications, or status issues.',
  commonlyOverlooked: 'Traveling internationally with a child without the other parent requires a formal consent letter.',
  whenToReview: 'Planning extended travel, sponsoring family, or acquiring assets outside Canada.'
}, {
  id: 'readiness',
  title: 'Personal Legal Readiness',
  icon: Shield,
  whyItMatters: 'Having legal documents is only half the battle; they must be accessible when needed. Storing them securely and ensuring your executor or attorney knows where they are prevents delays and added stress during emergencies.',
  commonlyOverlooked: 'Keeping the only original copy of a Will in a bank safety deposit box can cause issues if the executor cannot access the box after death.',
  whenToReview: 'Annually, to ensure documents are organized and contacts are up to date.'
}];
const BUSINESS_SECTIONS = [{
  id: 'setup',
  title: 'Business Setup & Ownership',
  icon: Building2,
  whyItMatters: 'Proper incorporation and a Shareholders Agreement protect your personal assets from business liabilities. Up-to-date minute books are legally required in Canada and essential for securing financing or selling your business.',
  commonlyOverlooked: 'Failing to file annual returns can lead to administrative dissolution of your corporation.',
  whenToReview: 'Starting up, bringing on new partners, or seeking investment.'
}, {
  id: 'finance',
  title: 'Financial Commitments & Liability',
  icon: Scale,
  whyItMatters: 'Personal guarantees and indemnities can pierce the corporate veil, putting your personal home and savings at risk for business debts.',
  commonlyOverlooked: 'Signing a commercial lease often requires a personal guarantee that outlasts your involvement in the business.',
  whenToReview: 'Taking on debt, signing commercial leases, or entering major vendor agreements.'
}, {
  id: 'contracts',
  title: 'Contracts, Commercial Terms & IP',
  icon: FileCheck,
  whyItMatters: 'Relying on informal or verbal agreements leaves your business vulnerable to disputes and non-payment. Standardized, legally reviewed contracts set clear expectations and protect your commercial interests.',
  commonlyOverlooked: 'Using templates from the US or other jurisdictions may include clauses that are unenforceable in Canada.',
  whenToReview: 'Launching new services, entering new markets, or creating original IP.'
}, {
  id: 'hr',
  title: 'People & Workplace Risk',
  icon: Users,
  whyItMatters: 'Misclassifying employees as independent contractors can lead to severe CRA penalties. Clear employment contracts and up-to-date policies ensure compliance with provincial Employment Standards.',
  commonlyOverlooked: 'Without a written termination clause, employees are entitled to common law reasonable notice, which can be up to 24 months of pay.',
  whenToReview: 'Hiring first employees, terminating staff, or updating compensation.'
}, {
  id: 'privacy',
  title: 'Privacy, Operations & Compliance',
  icon: Globe,
  whyItMatters: 'If you collect customer data, a PIPEDA-compliant Privacy Policy is mandatory. Adequate commercial general liability (CGL) and professional liability (E&O) insurance are critical to shield your business from lawsuits.',
  commonlyOverlooked: "Canada's Anti-Spam Legislation (CASL) requires express or implied consent before sending commercial emails.",
  whenToReview: 'Launching a website, collecting new data types, or expanding operations.'
}, {
  id: 'readiness',
  title: 'Business Legal Readiness',
  icon: Activity,
  whyItMatters: 'Organized legal records and proactive compliance tracking prevent minor administrative issues from becoming major legal hurdles during audits or due diligence.',
  commonlyOverlooked: 'Missing renewal deadlines for licenses or insurance can halt operations immediately.',
  whenToReview: 'Annually, or before a major transaction like a sale or audit.'
}];
const PERSONAL_QUESTIONS = [[{
  id: 'p_fam_1',
  text: 'Has your family situation changed recently (e.g., marriage, common-law, separation, new child)?',
  context: 'Major life events often invalidate existing legal planning or create new legal obligations.',
  options: ['Yes, recently changed', 'Anticipating a change soon', 'No changes'],
  scores: [40, 60, 100]
}, {
  id: 'p_fam_2',
  text: 'If living with a partner, do you have a formal Cohabitation Agreement or Prenuptial Agreement?',
  context: 'Common-law relationships have different property rights than marriages in most provinces. An agreement clarifies asset division.',
  options: ['Yes', 'No', 'Not applicable'],
  scores: [100, 0, 100]
}, {
  id: 'p_fam_3',
  text: 'If you are separated or divorced, do you have a finalized, legally binding Separation Agreement?',
  context: 'A Separation Agreement provides certainty on property division, support, and custody. Without one, disputes can drag on.',
  options: ['Yes', 'No', 'In progress', 'Not applicable'],
  scores: [100, 0, 50, 100]
}, {
  id: 'p_fam_4',
  text: 'Have you legally documented guardianship wishes for your minor children?',
  context: 'If both parents die without naming a guardian, the court decides who raises your children.',
  options: ['Yes', 'No', 'Not applicable'],
  scores: [100, 0, 100]
}], [{
  id: 'p_est_1',
  text: 'Do you have a signed Last Will and Testament that reflects your current wishes?',
  context: "Without a valid Will, your province's intestacy laws determine how your assets are distributed.",
  options: ['Yes, fully updated', 'Yes, but it needs updating', 'No, not yet'],
  scores: [100, 50, 0]
}, {
  id: 'p_est_2',
  text: 'Have you appointed a Power of Attorney for Property to manage your finances if you become incapacitated?',
  context: 'If you become incapacitated without a POA, someone must apply to the court to manage your finances.',
  options: ['Yes, legally documented', 'No', 'I am not sure'],
  scores: [100, 0, 0]
}, {
  id: 'p_est_3',
  text: 'Have you appointed a Power of Attorney for Personal Care (or Personal Directive) for healthcare decisions?',
  context: 'Without this, your family may face difficult decisions about your medical treatment without clear guidance.',
  options: ['Yes, legally documented', 'No', 'I am not sure'],
  scores: [100, 0, 0]
}, {
  id: 'p_est_4',
  text: 'Have you considered how your digital assets (online accounts, crypto, digital files) should be handled?',
  context: 'Digital assets are often lost or inaccessible to executors without specific instructions.',
  options: ['Yes, instructions are documented', 'No', 'Not applicable'],
  scores: [100, 0, 100]
}], [{
  id: 'p_prop_1',
  text: 'Is your home ownership structured jointly (Joint Tenancy) or individually (Tenants in Common)?',
  context: 'Joint Tenancy means the property automatically passes to the surviving owner, bypassing your Will. Tenants in Common allows you to leave your share to anyone.',
  options: ['Joint Tenancy', 'Tenants in Common', 'Sole Owner', 'I rent / Not applicable'],
  scores: [100, 100, 100, 100]
}, {
  id: 'p_prop_2',
  text: 'Are you planning to buy, sell, or refinance real estate in the next 12 months?',
  context: 'Real estate transactions require legal review of purchase agreements, title searches, and mortgage documents.',
  options: ['Yes', 'No'],
  scores: [50, 100]
}, {
  id: 'p_prop_3',
  text: 'If you rent, do you have a written lease agreement that complies with your provincial Residential Tenancies Act?',
  context: 'A written lease protects your rights as a tenant, including rules around rent increases and eviction procedures.',
  options: ['Yes', 'No', 'Not applicable'],
  scores: [100, 0, 100]
}, {
  id: 'p_prop_4',
  text: 'If you are a landlord, do you use standardized, legally compliant lease agreements?',
  context: 'Using outdated or non-compliant leases can make it difficult to enforce rules or evict problem tenants.',
  options: ['Yes', 'No', 'Not applicable'],
  scores: [100, 0, 100]
}], [{
  id: 'p_work_1',
  text: 'Do you have a written employment contract outlining severance and termination terms?',
  context: 'Without a written contract, you may be entitled to common law reasonable notice, which is often more generous than statutory minimums.',
  options: ['Yes', 'No', 'I am self-employed/retired'],
  scores: [100, 0, 100]
}, {
  id: 'p_work_2',
  text: 'Are you currently facing major debt, collection pressure, or considering bankruptcy?',
  context: 'Unresolved debt can lead to wage garnishment or asset seizure. Legal options exist to manage or restructure debt.',
  options: ['Yes', 'No'],
  scores: [0, 100]
}, {
  id: 'p_work_3',
  text: 'Are you involved in, or anticipating, a personal dispute or lawsuit (e.g., personal injury, contract dispute)?',
  context: 'Early legal intervention in disputes can often prevent costly litigation and secure better settlements.',
  options: ['Yes, currently involved', 'Anticipating one', 'No'],
  scores: [0, 40, 100]
}], [{
  id: 'p_spec_1',
  text: 'Are you planning to sponsor family members or foreign nationals for Canadian immigration?',
  context: 'Sponsorship involves strict legal and financial commitments that last for years, even if relationships change.',
  options: ['Yes', 'No'],
  scores: [50, 100]
}, {
  id: 'p_spec_2',
  text: 'Do you own property or significant assets outside of Canada?',
  context: 'Cross-border assets may require a separate Will in that jurisdiction and have complex tax implications.',
  options: ['Yes', 'No'],
  scores: [40, 100]
}, {
  id: 'p_spec_3',
  text: 'Are you a primary caregiver for a vulnerable dependant or adult with special needs?',
  context: 'Special needs trusts (Henson Trusts) and specific guardianship planning are required to protect their government benefits.',
  options: ['Yes', 'No'],
  scores: [50, 100]
}], [{
  id: 'p_read_1',
  text: 'Are your important legal documents stored securely and accessible to your executor or designated attorney?',
  context: 'If your executor cannot find your Will or POA documents when needed, it causes delays, added costs, and stress.',
  options: ['Yes', 'No', 'Some of them'],
  scores: [100, 0, 50]
}, {
  id: 'p_read_2',
  text: 'Would your family or emergency contacts know where to find your key legal documents if needed?',
  context: 'Communication is key. A perfectly drafted Will is useless if no one knows it exists or where to find it.',
  options: ['Yes, they know exactly where', 'They have a general idea', 'No'],
  scores: [100, 50, 0]
}, {
  id: 'p_read_3',
  text: 'How confident are you in your overall legal readiness if something unexpected happens tomorrow?',
  context: 'Confidence usually correlates with having organized, up-to-date legal protections in place.',
  options: ['Very confident', 'Somewhat confident', 'Not confident'],
  scores: [100, 50, 0]
}]];
const BUSINESS_QUESTIONS = [[{
  id: 'b_set_1',
  text: 'Is your business formally incorporated federally or provincially in Canada?',
  context: 'Incorporation creates a separate legal entity, protecting your personal assets from business liabilities.',
  options: ['Yes, incorporated', 'No, sole proprietorship', 'No, general partnership'],
  scores: [100, 40, 30]
}, {
  id: 'b_set_2',
  text: 'If you have co-founders or partners, do you have a signed Shareholders or Partnership Agreement?',
  context: 'Without an agreement, provincial default rules apply, which can lead to deadlock or costly disputes when partners disagree.',
  options: ['Yes, drafted by a lawyer', 'Yes, but it is informal', 'No', 'Not applicable (sole owner)'],
  scores: [100, 40, 0, 100]
}, {
  id: 'b_set_3',
  text: 'Are your corporate minute books and annual returns up to date with the government registry?',
  context: 'Falling behind can result in administrative dissolution, loss of limited liability protection, and difficulty securing financing.',
  options: ['Yes, fully up to date', 'No, we are behind', 'I do not know what that is', 'Not applicable'],
  scores: [100, 30, 0, 100]
}], [{
  id: 'b_fin_1',
  text: "Have you been asked to personally guarantee someone else's business debt or a commercial lease?",
  context: 'Personal guarantees bypass corporate limited liability, putting your personal assets (like your home) at risk if the business defaults.',
  options: ['Yes, and I signed it', 'Yes, but I am reviewing it', 'No'],
  scores: [30, 60, 100]
}, {
  id: 'b_fin_2',
  text: 'Have you signed agreements requiring you to indemnify another party?',
  context: "Indemnity clauses can make you financially responsible for the other party's losses or legal fees, often beyond your insurance limits.",
  options: ['Yes', 'I am not sure', 'No'],
  scores: [40, 30, 100]
}, {
  id: 'b_fin_3',
  text: 'Do you regularly sign commercial obligations or loans without legal review?',
  context: 'Unreviewed contracts often contain unfavorable terms, hidden fees, or restrictive covenants that can harm your business.',
  options: ['Yes, frequently', 'Sometimes', 'No, we review everything'],
  scores: [0, 50, 100]
}], [{
  id: 'b_con_1',
  text: 'Does your business rely on contracts that are reused without legal review?',
  context: "Reusing old or generic contracts can leave you exposed to outdated laws or terms that don't fit your current services.",
  options: ['Yes', 'Sometimes', 'No, we use updated templates'],
  scores: [0, 50, 100]
}, {
  id: 'b_con_2',
  text: 'Do you have written agreements with all key suppliers, vendors, and independent contractors?',
  context: 'Written agreements clarify pricing, delivery terms, liability, and termination rights. Without them, you have little recourse.',
  options: ['Yes', 'Most of them', 'No'],
  scores: [100, 60, 0]
}, {
  id: 'b_con_3',
  text: 'Have you registered trademarks for your brand name or logo with the Canadian Intellectual Property Office (CIPO)?',
  context: 'Trademark registration gives you exclusive rights to your brand across Canada and makes it easier to stop copycats.',
  options: ['Yes', 'Pending', 'No', 'Not applicable'],
  scores: [100, 70, 0, 100]
}], [{
  id: 'b_hr_1',
  text: 'Do all employees have signed employment contracts that comply with provincial Employment Standards?',
  context: 'Employment contracts clarify compensation, duties, and termination notice. Without them, you may owe more severance than expected.',
  options: ['Yes', 'Some do', 'No', 'No employees'],
  scores: [100, 50, 0, 100]
}, {
  id: 'b_hr_2',
  text: 'Do you have an up-to-date employee handbook including a Workplace Violence and Harassment Policy?',
  context: 'Provincial occupational health and safety laws require policies on workplace violence and harassment.',
  options: ['Yes', 'Needs updating', 'No', 'No employees'],
  scores: [100, 50, 0, 100]
}, {
  id: 'b_hr_3',
  text: 'Are you confident your independent contractors are properly classified to avoid CRA misclassification risks?',
  context: 'The CRA can reclassify contractors as employees retroactively, resulting in back taxes, CPP/EI premiums, penalties, and interest.',
  options: ['Yes', 'Not sure', 'No', 'No contractors'],
  scores: [100, 40, 0, 100]
}], [{
  id: 'b_priv_1',
  text: 'Do you collect personal information through your website or business operations?',
  context: 'Collecting personal data triggers obligations under PIPEDA and provincial privacy laws.',
  options: ['Yes', 'No'],
  scores: [50, 100]
}, {
  id: 'b_priv_2',
  text: 'Does your website have a compliant Privacy Policy and Terms of Use?',
  context: 'PIPEDA requires businesses that collect personal information to have a publicly available privacy policy.',
  options: ['Yes', 'No', 'No website'],
  scores: [100, 0, 100]
}, {
  id: 'b_priv_3',
  text: 'Do you have appropriate commercial general liability (CGL) and professional liability (E&O) insurance?',
  context: 'CGL protects against bodily injury/property damage claims. E&O covers professional mistakes. Without them, a claim could bankrupt you.',
  options: ['Yes', 'No', 'Not sure if coverage is adequate'],
  scores: [100, 0, 40]
}], [{
  id: 'b_read_1',
  text: 'Are your business legal records organized and easily accessible?',
  context: 'Disorganized records can delay financing, complicate audits, and increase legal fees during due diligence.',
  options: ['Yes', 'Somewhat', 'No'],
  scores: [100, 50, 0]
}, {
  id: 'b_read_2',
  text: 'Does your business have ongoing legal support for day-to-day questions?',
  context: 'Having a legal partner helps you catch small issues before they become expensive problems.',
  options: ['Yes', 'Occasionally', 'No'],
  scores: [100, 50, 0]
}, {
  id: 'b_read_3',
  text: 'Are important compliance deadlines and renewals (e.g., leases, licenses, corporate filings) being tracked?',
  context: 'Missing a renewal deadline can result in lost leases, lapsed licenses, or corporate dissolution.',
  options: ['Yes, systematically', 'Somewhat', 'No'],
  scores: [100, 50, 0]
}]];
// Helper function to filter questions based on setup data
function filterQuestions(questions: any[][], setupData: any, type: AssessmentType): any[][] {
  if (type === 'personal') {
    return questions.map(sectionQuestions => {
      return sectionQuestions.filter(q => {
        // Family branching
        if (q.id === 'p_fam_2' && setupData.relationship !== 'common-law' && setupData.relationship !== 'married') return false;
        if (q.id === 'p_fam_3' && setupData.relationship !== 'separated' && setupData.relationship !== 'divorced') return false;
        if (q.id === 'p_fam_4' && setupData.children === 'no') return false;
        // Property branching
        if (q.id === 'p_prop_1' && setupData.property === 'no') return false;
        if (q.id === 'p_prop_2' && setupData.property === 'no') return false;
        if (q.id === 'p_prop_3' && setupData.landlord === 'yes') return false; // if they are landlord, they might not be renting, but let's just use property='no' for tenants
        if (q.id === 'p_prop_3' && setupData.property === 'yes') return false; // owners don't need lease
        if (q.id === 'p_prop_4' && setupData.landlord === 'no') return false;
        // Special branching
        if (q.id === 'p_spec_1' && setupData.leavingCanada === 'no') return false;
        return true;
      });
    });
  } else {
    return questions.map(sectionQuestions => {
      return sectionQuestions.filter(q => {
        // Setup branching
        if (q.id === 'b_set_2' && setupData.businessType === 'Sole Proprietorship') return false;
        if (q.id === 'b_set_3' && setupData.businessType === 'Sole Proprietorship') return false;
        // Contracts branching
        if (q.id === 'b_con_3' && setupData.createsIP === 'no') return false;
        // HR branching
        if ((q.id === 'b_hr_1' || q.id === 'b_hr_2') && setupData.employees === 'Just me (0)') return false;
        // Privacy branching
        if (q.id === 'b_priv_2' && setupData.hasWebsite === 'no') return false;
        return true;
      });
    });
  }
}
// Helper function to calculate scores from answers
function calculateScores(answers: Record<string, string>, type: AssessmentType, setupData: any): {
  overall: number;
  sections: number[];
} {
  const allQuestions = type === 'personal' ? PERSONAL_QUESTIONS : BUSINESS_QUESTIONS;
  const filteredQuestions = filterQuestions(allQuestions, setupData, type);
  const sectionScores: number[] = [];
  filteredQuestions.forEach(sectionQuestions => {
    if (sectionQuestions.length === 0) {
      sectionScores.push(100); // Empty section = perfect score
      return;
    }
    let sectionTotal = 0;
    let questionCount = 0;
    sectionQuestions.forEach(q => {
      const answer = answers[q.id];
      if (answer !== undefined) {
        const optionIndex = q.options.indexOf(answer);
        if (optionIndex !== -1 && q.scores) {
          sectionTotal += q.scores[optionIndex];
          questionCount++;
        }
      }
    });
    const sectionScore = questionCount > 0 ? Math.round(sectionTotal / questionCount) : 0;
    sectionScores.push(sectionScore);
  });
  // Calculate overall as average of section scores
  const overall = sectionScores.length > 0 ? Math.round(sectionScores.reduce((a, b) => a + b, 0) / sectionScores.length) : 0;
  return {
    overall,
    sections: sectionScores
  };
}
// Helper function to generate dynamic action plan from answers
function generateDynamicActionPlan(answers: Record<string, string>, type: AssessmentType, setupData: any): {
  id: string;
  text: string;
  completed: boolean;
  category: string;
  priority: 'high' | 'medium' | 'low';
  actionUrl: string;
  actionText: string;
  effort: 'Low' | 'Medium' | 'High';
  time: string;
}[] {
  const items: any[] = [];
  let idCounter = 1;
  if (type === 'personal') {
    // Family
    if (answers.p_fam_1 === 'Yes, recently changed' || answers.p_fam_1 === 'Anticipating a change soon') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Review legal documents due to recent/upcoming family changes',
        completed: false,
        category: 'Family',
        priority: 'high',
        actionUrl: '/legal-help',
        actionText: 'Consult Lawyer',
        effort: 'Medium',
        time: '1 hour'
      });
    }
    if (answers.p_fam_2 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Draft a Cohabitation or Prenuptial Agreement',
        completed: false,
        category: 'Family',
        priority: 'medium',
        actionUrl: '/all-templates',
        actionText: 'Find Template',
        effort: 'High',
        time: '2-3 hours'
      });
    }
    if (answers.p_fam_3 === 'No' || answers.p_fam_3 === 'In progress') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Finalize your Separation Agreement',
        completed: false,
        category: 'Family',
        priority: 'high',
        actionUrl: '/legal-help',
        actionText: 'Get Legal Help',
        effort: 'High',
        time: '3-5 hours'
      });
    }
    if (answers.p_fam_4 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Document guardianship wishes for minor children',
        completed: false,
        category: 'Family',
        priority: 'high',
        actionUrl: '/wills',
        actionText: 'Update Will',
        effort: 'Low',
        time: '30 mins'
      });
    }
    // Estate
    if (answers.p_est_1 === 'No, not yet') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Draft your Last Will and Testament',
        completed: false,
        category: 'Estate Planning',
        priority: 'high',
        actionUrl: '/wills',
        actionText: 'Create Will',
        effort: 'Medium',
        time: '1-2 hours'
      });
    } else if (answers.p_est_1 === 'Yes, but it needs updating') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Update your Will to reflect current wishes',
        completed: false,
        category: 'Estate Planning',
        priority: 'medium',
        actionUrl: '/wills',
        actionText: 'Update Will',
        effort: 'Medium',
        time: '1 hour'
      });
    }
    if (answers.p_est_2 === 'No' || answers.p_est_2 === 'I am not sure') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Create a Power of Attorney for Property',
        completed: false,
        category: 'Estate Planning',
        priority: 'high',
        actionUrl: '/all-templates',
        actionText: 'Create POA',
        effort: 'Low',
        time: '30 mins'
      });
    }
    if (answers.p_est_3 === 'No' || answers.p_est_3 === 'I am not sure') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Create a Power of Attorney for Personal Care',
        completed: false,
        category: 'Estate Planning',
        priority: 'high',
        actionUrl: '/all-templates',
        actionText: 'Create POA',
        effort: 'Low',
        time: '30 mins'
      });
    }
    if (answers.p_est_4 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Document instructions for digital assets',
        completed: false,
        category: 'Estate Planning',
        priority: 'low',
        actionUrl: '/ai-copilot',
        actionText: 'Ask AI Copilot',
        effort: 'Low',
        time: '30 mins'
      });
    }
    // Property
    if (answers.p_prop_2 === 'Yes') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Review real estate transaction documents',
        completed: false,
        category: 'Property',
        priority: 'medium',
        actionUrl: '/legal-help',
        actionText: 'Consult Lawyer',
        effort: 'Medium',
        time: '1-2 hours'
      });
    }
    if (answers.p_prop_3 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Obtain a written lease agreement from your landlord',
        completed: false,
        category: 'Property',
        priority: 'medium',
        actionUrl: '/legal-help',
        actionText: 'Get Advice',
        effort: 'Low',
        time: '30 mins'
      });
    }
    if (answers.p_prop_4 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Use standardized lease agreements for your tenants',
        completed: false,
        category: 'Property',
        priority: 'high',
        actionUrl: '/all-templates',
        actionText: 'Find Lease Template',
        effort: 'Low',
        time: '30 mins'
      });
    }
    // Work & Money
    if (answers.p_work_1 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Request a written employment contract',
        completed: false,
        category: 'Work & Money',
        priority: 'low',
        actionUrl: '/ai-copilot',
        actionText: 'Ask AI Copilot',
        effort: 'Low',
        time: '15 mins'
      });
    }
    if (answers.p_work_2 === 'Yes') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Explore legal options for debt management',
        completed: false,
        category: 'Work & Money',
        priority: 'high',
        actionUrl: '/legal-help',
        actionText: 'Get Legal Help',
        effort: 'High',
        time: '2 hours'
      });
    }
    if (answers.p_work_3 === 'Yes, currently involved' || answers.p_work_3 === 'Anticipating one') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Seek early legal intervention for your dispute',
        completed: false,
        category: 'Work & Money',
        priority: 'high',
        actionUrl: '/legal-help',
        actionText: 'Consult Lawyer',
        effort: 'High',
        time: '2 hours'
      });
    }
    // Special
    if (answers.p_spec_1 === 'Yes') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Review immigration sponsorship obligations',
        completed: false,
        category: 'Special Situations',
        priority: 'medium',
        actionUrl: '/legal-help',
        actionText: 'Get Advice',
        effort: 'Medium',
        time: '1 hour'
      });
    }
    if (answers.p_spec_2 === 'Yes') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Review cross-border asset planning',
        completed: false,
        category: 'Special Situations',
        priority: 'medium',
        actionUrl: '/legal-help',
        actionText: 'Consult Lawyer',
        effort: 'High',
        time: '2 hours'
      });
    }
    if (answers.p_spec_3 === 'Yes') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Set up special needs trust or guardianship planning',
        completed: false,
        category: 'Special Situations',
        priority: 'high',
        actionUrl: '/legal-help',
        actionText: 'Consult Lawyer',
        effort: 'High',
        time: '3 hours'
      });
    }
    // Readiness
    if (answers.p_read_1 === 'No' || answers.p_read_1 === 'Some of them') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Organize legal documents in a secure location',
        completed: false,
        category: 'Readiness',
        priority: 'medium',
        actionUrl: '/my-documents',
        actionText: 'Upload to Hub',
        effort: 'Low',
        time: '1 hour'
      });
    }
    if (answers.p_read_2 === 'No' || answers.p_read_2 === 'They have a general idea') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Share document locations with emergency contacts',
        completed: false,
        category: 'Readiness',
        priority: 'medium',
        actionUrl: '/my-account',
        actionText: 'Update Contacts',
        effort: 'Low',
        time: '15 mins'
      });
    }
  } else {
    // Business
    // Setup
    if (answers.b_set_1 === 'No, sole proprietorship' || answers.b_set_1 === 'No, general partnership') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Consider incorporating your business',
        completed: false,
        category: 'Setup',
        priority: 'high',
        actionUrl: '/legal-help',
        actionText: 'Consult Lawyer',
        effort: 'High',
        time: '3-5 hours'
      });
    }
    if (answers.b_set_2 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Draft a Shareholders or Partnership Agreement',
        completed: false,
        category: 'Setup',
        priority: 'high',
        actionUrl: '/all-templates',
        actionText: 'Find Template',
        effort: 'High',
        time: '2-4 hours'
      });
    } else if (answers.b_set_2 === 'Yes, but it is informal') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Formalize your Partnership Agreement',
        completed: false,
        category: 'Setup',
        priority: 'medium',
        actionUrl: '/legal-help',
        actionText: 'Get Legal Review',
        effort: 'Medium',
        time: '1-2 hours'
      });
    }
    if (answers.b_set_3 === 'No, we are behind' || answers.b_set_3 === 'I do not know what that is') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Update corporate minute books and file annual returns',
        completed: false,
        category: 'Setup',
        priority: 'medium',
        actionUrl: '/legal-help',
        actionText: 'Consult Lawyer',
        effort: 'Medium',
        time: '1-2 hours'
      });
    }
    // Finance
    if (answers.b_fin_1 === 'Yes, and I signed it' || answers.b_fin_1 === 'Yes, but I am reviewing it') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Review personal guarantee exposure',
        completed: false,
        category: 'Finance',
        priority: 'high',
        actionUrl: '/legal-help',
        actionText: 'Get Advice',
        effort: 'Medium',
        time: '1 hour'
      });
    }
    if (answers.b_fin_2 === 'Yes' || answers.b_fin_2 === 'I am not sure') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Review indemnity clauses in your contracts',
        completed: false,
        category: 'Finance',
        priority: 'medium',
        actionUrl: '/ai-copilot',
        actionText: 'Ask AI Copilot',
        effort: 'Medium',
        time: '1 hour'
      });
    }
    if (answers.b_fin_3 === 'Yes, frequently' || answers.b_fin_3 === 'Sometimes') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Establish a legal review process for commercial obligations',
        completed: false,
        category: 'Finance',
        priority: 'high',
        actionUrl: '/legal-help',
        actionText: 'Consult Lawyer',
        effort: 'Medium',
        time: '2 hours'
      });
    }
    // Contracts
    if (answers.b_con_1 === 'Yes' || answers.b_con_1 === 'Sometimes') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Update and standardize client service agreements',
        completed: false,
        category: 'Contracts',
        priority: 'high',
        actionUrl: '/all-templates',
        actionText: 'Find Template',
        effort: 'Medium',
        time: '1-2 hours'
      });
    }
    if (answers.b_con_2 === 'Most of them' || answers.b_con_2 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Create written agreements with all key suppliers',
        completed: false,
        category: 'Contracts',
        priority: 'medium',
        actionUrl: '/all-templates',
        actionText: 'View Templates',
        effort: 'Medium',
        time: '1-3 hours'
      });
    }
    if (answers.b_con_3 === 'No' || answers.b_con_3 === 'Pending') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Register trademarks for brand protection',
        completed: false,
        category: 'Contracts & IP',
        priority: 'low',
        actionUrl: '/legal-help',
        actionText: 'Get Guidance',
        effort: 'Medium',
        time: '1-2 hours'
      });
    }
    // HR
    if (answers.b_hr_1 === 'Some do' || answers.b_hr_1 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Ensure all employees have signed employment contracts',
        completed: false,
        category: 'HR',
        priority: 'high',
        actionUrl: '/all-templates',
        actionText: 'View HR Templates',
        effort: 'High',
        time: '2-4 hours'
      });
    }
    if (answers.b_hr_2 === 'Needs updating' || answers.b_hr_2 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Create or update employee handbook',
        completed: false,
        category: 'HR',
        priority: 'high',
        actionUrl: '/all-templates',
        actionText: 'Find Policy Templates',
        effort: 'Medium',
        time: '2-3 hours'
      });
    }
    if (answers.b_hr_3 === 'No' || answers.b_hr_3 === 'Not sure') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Review contractor classifications',
        completed: false,
        category: 'HR',
        priority: 'medium',
        actionUrl: '/legal-help',
        actionText: 'Get Expert Advice',
        effort: 'Medium',
        time: '1 hour'
      });
    }
    // Privacy
    if (answers.b_priv_1 === 'Yes' && answers.b_priv_2 === 'No') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Create a PIPEDA-compliant Privacy Policy',
        completed: false,
        category: 'Privacy',
        priority: 'high',
        actionUrl: '/all-templates',
        actionText: 'Create Policy',
        effort: 'Low',
        time: '30 mins'
      });
    }
    if (answers.b_priv_3 === 'No' || answers.b_priv_3 === 'Not sure if coverage is adequate') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Review and obtain adequate CGL and E&O insurance',
        completed: false,
        category: 'Privacy & Compliance',
        priority: 'high',
        actionUrl: '/legal-help',
        actionText: 'Get Advice',
        effort: 'Medium',
        time: '1 hour'
      });
    }
    // Readiness
    if (answers.b_read_1 === 'No' || answers.b_read_1 === 'Somewhat') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Organize business legal records',
        completed: false,
        category: 'Readiness',
        priority: 'medium',
        actionUrl: '/my-documents',
        actionText: 'Upload to Hub',
        effort: 'Low',
        time: '1-2 hours'
      });
    }
    if (answers.b_read_2 === 'No' || answers.b_read_2 === 'Occasionally') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Establish ongoing legal support',
        completed: false,
        category: 'Readiness',
        priority: 'low',
        actionUrl: '/legal-help',
        actionText: 'Explore Options',
        effort: 'Low',
        time: '30 mins'
      });
    }
    if (answers.b_read_3 === 'No' || answers.b_read_3 === 'Somewhat') {
      items.push({
        id: `a${idCounter++}`,
        text: 'Implement a compliance tracking system',
        completed: false,
        category: 'Readiness',
        priority: 'medium',
        actionUrl: '/ai-copilot',
        actionText: 'Ask AI Copilot',
        effort: 'Low',
        time: '1 hour'
      });
    }
  }
  return items;
}
// Helper function to get section-specific summary based on answers
function getSectionSummary(sectionId: string, answers: Record<string, string>, type: AssessmentType, score: number): string {
  if (type === 'personal') {
    switch (sectionId) {
      case 'family':
        if (answers.p_fam_3 === 'No') return 'You need a Separation Agreement to finalize your separation.';
        if (answers.p_fam_4 === 'No') return 'Guardianship wishes for your children are not documented.';
        if (answers.p_fam_2 === 'No') return 'Consider a Cohabitation Agreement to protect your interests.';
        return 'Your family legal matters are well-documented.';
      case 'estate':
        if (answers.p_est_1 === 'No, not yet') return 'You do not have a Will yet — this is your most critical gap.';
        if (answers.p_est_1 === 'Yes, but it needs updating') return 'Your Will needs updating to reflect your current situation.';
        if (answers.p_est_2 === 'No' || answers.p_est_3 === 'No') return 'Missing Power of Attorney documents for incapacity planning.';
        return 'Your estate planning foundation is solid.';
      case 'property':
        if (answers.p_prop_3 === 'No') return 'You need a written lease to protect your tenant rights.';
        if (answers.p_prop_4 === 'No') return 'You need standardized lease agreements for your tenants.';
        return 'Your property arrangements are properly structured.';
      case 'work_money':
        if (answers.p_work_2 === 'Yes') return 'Major debt issues require immediate legal/financial attention.';
        if (answers.p_work_3 === 'Yes, currently involved') return 'Active disputes require legal intervention.';
        if (answers.p_work_1 === 'No') return 'Missing employment contract — your severance rights are unclear.';
        return 'Your personal risk management is in good shape.';
      case 'special':
        if (answers.p_spec_1 === 'Yes') return 'Immigration sponsorship involves strict legal commitments.';
        if (answers.p_spec_3 === 'Yes') return 'Special needs trusts may be required for your dependants.';
        return 'Special situations are managed effectively.';
      case 'readiness':
        if (answers.p_read_1 === 'No') return 'Your legal documents are not properly stored or accessible.';
        if (answers.p_read_2 === 'No') return 'Emergency contacts do not know where to find your documents.';
        return 'Your documents are well-organized and accessible.';
      default:
        return score >= 70 ? 'Strong Foundation' : score >= 40 ? 'Needs Review' : 'Action Recommended';
    }
  } else {
    switch (sectionId) {
      case 'setup':
        if (answers.b_set_1 === 'No, sole proprietorship' || answers.b_set_1 === 'No, general partnership') return 'Not incorporated — your personal assets are at risk.';
        if (answers.b_set_2 === 'No') return 'Missing Shareholders Agreement — vulnerable to partner disputes.';
        if (answers.b_set_3 !== 'Yes, fully up to date') return 'Corporate records are behind — risking compliance issues.';
        return 'Your corporate structure is properly maintained.';
      case 'finance':
        if (answers.b_fin_1 === 'Yes, and I signed it') return 'Personal guarantees put your personal assets at risk.';
        if (answers.b_fin_3 === 'Yes, frequently') return 'Signing without review exposes you to unfavorable terms.';
        return 'Financial commitments are well managed.';
      case 'contracts':
        if (answers.b_con_1 === 'Yes') return 'Reusing old contracts leaves you exposed to outdated terms.';
        if (answers.b_con_2 === 'No') return 'Missing supplier/contractor agreements — vulnerable to disruption.';
        return 'Your commercial contracts are in good shape.';
      case 'hr':
        if (answers.b_hr_1 === 'No' || answers.b_hr_1 === 'Some do') return 'Missing employee contracts — severance exposure is high.';
        if (answers.b_hr_2 === 'No') return 'No workplace policies — non-compliant with safety regulations.';
        if (answers.b_hr_3 === 'No') return 'Contractor misclassification risk — CRA penalties possible.';
        return 'Your HR practices are compliant and well-documented.';
      case 'privacy':
        if (answers.b_priv_2 === 'No') return 'Missing Privacy Policy — non-compliant with PIPEDA.';
        if (answers.b_priv_3 === 'No') return 'No liability insurance — one lawsuit could bankrupt the business.';
        return 'Your privacy and compliance protections are solid.';
      case 'readiness':
        if (answers.b_read_1 === 'No') return 'Disorganized records can delay financing and complicate audits.';
        if (answers.b_read_3 === 'No') return 'Missing renewal deadlines can halt operations immediately.';
        return 'Your business readiness is strong.';
      default:
        return score >= 70 ? 'Strong Foundation' : score >= 40 ? 'Needs Review' : 'Action Recommended';
    }
  }
}
function AnimatedScore({
  score
}: {
  score: number;
}) {
  const [displayScore, setDisplayScore] = useState(0);
  useEffect(() => {
    let start = 0;
    const end = score;
    if (start === end) {
      setDisplayScore(end);
      return;
    }
    const totalDuration = 1500;
    const incrementTime = totalDuration / (end || 1);
    const timer = setInterval(() => {
      start += 1;
      setDisplayScore(start);
      if (start >= end) clearInterval(timer);
    }, incrementTime);
    return () => clearInterval(timer);
  }, [score]);
  const label = score >= 70 ? 'Strong' : score >= 40 ? 'Review' : 'Action';
  const color = score >= 70 ? 'text-green-500' : score >= 40 ? 'text-amber-500' : 'text-destructive';
  return <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-713">
      <span className="text-4xl font-bold text-primary" data-id="element-714">{displayScore}</span>
      <span className={`text-sm font-bold uppercase tracking-wider ${color}`} data-id="element-715">
        {label}
      </span>
      <span className="text-xs font-medium text-secondary uppercase tracking-wider mt-1" data-id="element-716">
        / 100
      </span>
    </div>;
}
export function LegalHealthCheck({
  embedded = false,
  initialAssessmentType,
  initialViewState,
  onExit
}: LegalHealthCheckProps = {}) {
  const navigate = useNavigate();
  const [viewState, setViewState] = useState<ViewState>(initialViewState || 'landing');
  const [assessmentType, setAssessmentType] = useState<AssessmentType>(initialAssessmentType || 'personal');
  const [direction, setDirection] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  // Setup State
  const [setupData, setSetupData] = useState<any>({
    province: 'Ontario',
    // Personal
    relationship: 'single',
    children: 'no',
    property: 'no',
    landlord: 'no',
    leavingCanada: 'no',
    // Business
    businessName: '',
    businessType: 'Corporation',
    employees: 'Just me (0)',
    hasWebsite: 'yes',
    createsIP: 'yes',
    inDispute: 'no'
  });
  // Wizard State
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showSaveToast, setShowSaveToast] = useState(false);
  // Results State
  const [score, setScore] = useState(0);
  const [sectionScores, setSectionScores] = useState<number[]>([]);
  const [actionPlanItems, setActionPlanItems] = useState<{
    id: string;
    text: string;
    completed: boolean;
    category: string;
    priority: 'high' | 'medium' | 'low';
    actionUrl: string;
    actionText: string;
    effort: 'Low' | 'Medium' | 'High';
    time: string;
  }[]>([]);
  // History State
  const [historyItems, setHistoryItems] = useState([{
    id: '1',
    type: 'personal',
    date: 'Oct 12, 2025',
    status: 'completed',
    score: 62,
    topFlag: 'Estate Planning'
  }, {
    id: '2',
    type: 'business',
    date: 'Today',
    status: 'draft',
    score: 40,
    topFlag: 'Contracts'
  }]);
  // Modal States
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  // Toast State
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 3000);
  };
  const handleNavigateToAICopilot = () => navigate('/ai-copilot');
  const handleNavigate = (path: string) => {
    navigate(path);
  };
  const startSetup = (assessmentType: AssessmentType) => {
    setAssessmentType(assessmentType);
    setViewState('setup');
    setCurrentSectionIndex(0);
    setAnswers({});
  };
  const startWizard = () => {
    setViewState('wizard');
  };
  const saveDraft = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      setShowSaveToast(true);
      setTimeout(() => setShowSaveToast(false), 3000);
      const newHistory = [...historyItems];
      const existingDraftIndex = newHistory.findIndex(h => h.status === 'draft' && h.type === assessmentType);
      const currentScore = Math.round(currentSectionIndex / (assessmentType === 'personal' ? PERSONAL_SECTIONS.length : BUSINESS_SECTIONS.length) * 100);
      if (existingDraftIndex >= 0) {
        newHistory[existingDraftIndex].date = 'Just now';
        newHistory[existingDraftIndex].score = currentScore;
      } else {
        newHistory.unshift({
          id: Date.now().toString(),
          type: assessmentType,
          date: 'Just now',
          status: 'draft',
          score: currentScore,
          topFlag: 'In Progress'
        });
      }
      setHistoryItems(newHistory);
    }, 800);
  };
  const generateActionPlan = () => {
    const scores = calculateScores(answers, assessmentType, setupData);
    setScore(scores.overall);
    setSectionScores(scores.sections);
    const items = generateDynamicActionPlan(answers, assessmentType, setupData);
    setActionPlanItems(items);
  };
  const nextSection = () => {
    const sections = assessmentType === 'personal' ? PERSONAL_SECTIONS : BUSINESS_SECTIONS;
    if (currentSectionIndex < sections.length - 1) {
      setDirection(1);
      setCurrentSectionIndex(prev => prev + 1);
      window.scrollTo(0, 0);
    } else {
      generateActionPlan();
      setViewState('results');
      window.scrollTo(0, 0);
      const newHistory = [...historyItems];
      const draftIndex = newHistory.findIndex(h => h.status === 'draft' && h.type === assessmentType);
      const scores = calculateScores(answers, assessmentType, setupData);
      // Find weakest category for top flag
      const weakestIdx = scores.sections.indexOf(Math.min(...scores.sections));
      const topFlag = sections[weakestIdx]?.title || 'General';
      if (draftIndex >= 0) {
        newHistory[draftIndex] = {
          ...newHistory[draftIndex],
          status: 'completed',
          score: scores.overall,
          date: 'Just now',
          topFlag
        };
      } else {
        newHistory.unshift({
          id: Date.now().toString(),
          type: assessmentType,
          date: 'Just now',
          status: 'completed',
          score: scores.overall,
          topFlag
        });
      }
      setHistoryItems(newHistory);
    }
  };
  const prevSection = () => {
    if (currentSectionIndex > 0) {
      setDirection(-1);
      setCurrentSectionIndex(prev => prev - 1);
      window.scrollTo(0, 0);
    } else {
      setViewState('setup');
    }
  };
  const handleBackToOverview = () => {
    if (onExit) {
      onExit();
    } else {
      setViewState('landing');
    }
  };
  const handleAnswer = (questionId: string, answer: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };
  const toggleActionItem = (id: string) => {
    setActionPlanItems(prev => prev.map(item => item.id === id ? {
      ...item,
      completed: !item.completed
    } : item));
  };
  // Dynamic Theme Classes based on Assessment Type
  const themeBg = assessmentType === 'personal' ? 'bg-violetBlue' : 'bg-turquoise';
  const themeText = assessmentType === 'personal' ? 'text-violetBlue' : 'text-turquoise';
  const themeBorder = assessmentType === 'personal' ? 'border-violetBlue' : 'border-turquoise';
  const themeHoverBg = assessmentType === 'personal' ? 'hover:bg-[#6844E8]' : 'hover:bg-turquoise/90';
  const themeLightBg = assessmentType === 'personal' ? 'bg-aliceBlue' : 'bg-turquoise/10';
  const themeBorderLight = assessmentType === 'personal' ? 'border-violetBlue/20' : 'border-turquoise/20';
  const renderLanding = () => <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-id="element-717">
      <div className="text-center mb-12" data-id="element-718">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-aliceBlue rounded-full mb-6" data-id="element-719">
          <Activity className="w-8 h-8 text-violetBlue" data-id="element-720" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-primary mb-3" data-id="element-721">
          Annual Legal Wellness Check
        </h1>

        <p className="text-sm text-secondary max-w-xl mx-auto italic" data-id="element-722">
          This assessment helps surface common legal issues worth reviewing. It
          is for informational purposes only and does not replace legal advice.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-16" data-id="element-723">
        {/* Personal Card */}
        <div className="bg-white border border-borderColor rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow flex flex-col" data-id="element-724">
          <div className="flex items-start gap-4 mb-6" data-id="element-725">
            <div className="p-3 bg-aliceBlue rounded-xl" data-id="element-726">
              <Activity className="w-8 h-8 text-violetBlue" data-id="element-727" />
            </div>
            <div data-id="element-728">
              <h2 className="text-xl font-bold text-primary mb-1" data-id="element-729">
                Personal Legal Health Check
              </h2>
              <div className="flex items-center gap-2 text-sm text-secondary" data-id="element-730">
                <Clock className="w-4 h-4" data-id="element-731" />
                <span data-id="element-732">~10 min to complete</span>
              </div>
            </div>
          </div>
          <p className="text-secondary mb-8 flex-grow leading-relaxed" data-id="element-733">
            Review your family changes, estate planning, property ownership, and
            personal risk. Ensure your loved ones are protected and your
            documents are ready for the unexpected.
          </p>
          <button onClick={() => startSetup('personal')} className="w-full bg-violetBlue hover:bg-[#6844E8] text-white font-medium py-3.5 px-4 rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-734">
            Start Personal Check
            <ChevronRight className="w-5 h-5" data-id="element-735" />
          </button>
        </div>

        {/* Business Card */}
        <div className="bg-white border border-borderColor rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow flex flex-col" data-id="element-736">
          <div className="flex items-start gap-4 mb-6" data-id="element-737">
            <div className="p-3 bg-turquoise/10 rounded-xl" data-id="element-738">
              <Building2 className="w-8 h-8 text-turquoise" data-id="element-739" />
            </div>
            <div data-id="element-740">
              <h2 className="text-xl font-bold text-primary mb-1" data-id="element-741">
                Business Legal Health Check
              </h2>
              <div className="flex items-center gap-2 text-sm text-secondary" data-id="element-742">
                <Clock className="w-4 h-4" data-id="element-743" />
                <span data-id="element-744">~15 min to complete</span>
              </div>
            </div>
          </div>
          <p className="text-secondary mb-8 flex-grow leading-relaxed" data-id="element-745">
            Review your corporate structure, financial commitments, contracts,
            HR compliance, and privacy policies. Identify hidden liabilities
            before they become costly problems.
          </p>
          <button onClick={() => startSetup('business')} className="w-full bg-turquoise hover:bg-turquoise/90 text-white font-medium py-3.5 px-4 rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-746">
            Start Business Check
            <ChevronRight className="w-5 h-5" data-id="element-747" />
          </button>
        </div>
      </div>

      {/* How it works & History */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-748">
        <div className="lg:col-span-2" data-id="element-749">
          <h3 className="text-xl font-bold text-primary mb-6" data-id="element-750">How it works</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-751">
            <div className="bg-aliceBlue rounded-xl p-6 relative overflow-hidden" data-id="element-752">
              <div className="text-6xl font-bold text-white absolute -right-2 -bottom-4 opacity-50 select-none" data-id="element-753">
                1
              </div>
              <div className="relative z-10" data-id="element-754">
                <FileText className="w-6 h-6 text-violetBlue mb-4" data-id="element-755" />
                <h4 className="font-semibold text-primary mb-2" data-id="element-756">
                  Answer Questions
                </h4>
                <p className="text-sm text-secondary" data-id="element-757">
                  Complete a guided questionnaire tailored to your situation.
                </p>
              </div>
            </div>
            <div className="bg-aliceBlue rounded-xl p-6 relative overflow-hidden" data-id="element-758">
              <div className="text-6xl font-bold text-white absolute -right-2 -bottom-4 opacity-50 select-none" data-id="element-759">
                2
              </div>
              <div className="relative z-10" data-id="element-760">
                <Activity className="w-6 h-6 text-violetBlue mb-4" data-id="element-761" />
                <h4 className="font-semibold text-primary mb-2" data-id="element-762">
                  Get Your Score
                </h4>
                <p className="text-sm text-secondary" data-id="element-763">
                  Instantly see your legal health score and specific risk areas.
                </p>
              </div>
            </div>
            <div className="bg-aliceBlue rounded-xl p-6 relative overflow-hidden" data-id="element-764">
              <div className="text-6xl font-bold text-white absolute -right-2 -bottom-4 opacity-50 select-none" data-id="element-765">
                3
              </div>
              <div className="relative z-10" data-id="element-766">
                <CheckCircle className="w-6 h-6 text-violetBlue mb-4" data-id="element-767" />
                <h4 className="font-semibold text-primary mb-2" data-id="element-768">Take Action</h4>
                <p className="text-sm text-secondary" data-id="element-769">
                  Receive a personalized plan with prioritized next steps.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-1" data-id="element-770">
          <div className="flex items-center justify-between mb-6" data-id="element-771">
            <h3 className="text-xl font-bold text-primary" data-id="element-772">
              Saved Assessments
            </h3>
            <button onClick={() => setViewState('history')} className="text-sm text-violetBlue hover:underline font-medium" data-id="element-773">
              View All
            </button>
          </div>
          <div className="bg-white border border-borderColor rounded-xl shadow-sm overflow-hidden" data-id="element-774">
            {historyItems.slice(0, 3).map(item => <div key={item.id} className="p-4 border-b border-borderColor hover:bg-aliceBlue transition-colors cursor-pointer last:border-b-0" onClick={() => {
            setAssessmentType(item.type as AssessmentType);
            if (item.status === 'completed') {
              generateActionPlan();
              setViewState('results');
            } else {
              startSetup(item.type as AssessmentType);
            }
          }} data-id="element-775">
                <div className="flex items-center justify-between mb-2" data-id="element-776">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${item.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-777">
                    {item.status === 'completed' ? 'Completed' : 'Draft'}
                  </span>
                  <span className="text-xs text-secondary" data-id="element-778">{item.date}</span>
                </div>
                <h4 className="font-semibold text-primary text-sm mb-1 capitalize" data-id="element-779">
                  {item.type} Health Check
                </h4>
                <div className="flex items-center gap-2" data-id="element-780">
                  <div className="w-full bg-borderColor rounded-full h-1.5" data-id="element-781">
                    <div className={`${item.status === 'completed' ? item.score >= 70 ? 'bg-green-500' : item.score >= 40 ? 'bg-amber-500' : 'bg-destructive' : 'bg-violetBlue'} h-1.5 rounded-full`} style={{
                  width: `${item.score}%`
                }} data-id="element-782"></div>
                  </div>
                  <span className="text-xs font-medium text-primary" data-id="element-783">
                    {item.status === 'completed' ? `${item.score}/100` : `${item.score}%`}
                  </span>
                </div>
              </div>)}
            {historyItems.length === 0 && <div className="p-6 text-center text-secondary text-sm" data-id="element-784">
                No assessments saved yet.
              </div>}
          </div>
        </div>
      </div>
    </div>;
  const renderSetup = () => <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-id="element-785">
      <button onClick={handleBackToOverview} className="flex items-center gap-2 text-secondary hover:text-primary transition-colors mb-8 text-sm font-medium" data-id="element-786">
        <ArrowLeft className="w-4 h-4" data-id="element-787" />
        {onExit ? 'Back to Dashboard' : 'Back to Overview'}
      </button>

      <div className="bg-white border border-borderColor rounded-2xl p-6 sm:p-10 shadow-sm" data-id="element-788">
        <div className="flex items-center gap-4 mb-6" data-id="element-789">
          <div className={`p-3 rounded-xl ${themeLightBg}`} data-id="element-790">
            {assessmentType === 'personal' ? <Activity className="w-8 h-8 text-violetBlue" data-id="element-791" /> : <Building2 className="w-8 h-8 text-turquoise" data-id="element-792" />}
          </div>
          <div data-id="element-793">
            <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-2" data-id="element-794">
              {assessmentType === 'personal' ? 'Personal' : 'Business'} Legal
              Health Check
            </h1>
            <div className="flex items-center gap-4 text-sm text-secondary" data-id="element-795">
              <span className="flex items-center gap-1" data-id="element-796">
                <Clock className="w-4 h-4" data-id="element-797" /> ~
                {assessmentType === 'personal' ? '10' : '15'} min
              </span>
              <span className="flex items-center gap-1" data-id="element-798">
                <Save className="w-4 h-4" data-id="element-799" /> Auto-saves progress
              </span>
            </div>
          </div>
        </div>

        <div className={`bg-amber-50 border-l-4 border-amber-500 p-5 rounded-r-lg mb-8`} data-id="element-800">
          <div className="flex items-start gap-3" data-id="element-801">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-802" />
            <div data-id="element-803">
              <h4 className="font-bold text-primary mb-2 text-sm" data-id="element-804">
                Important Legal Disclaimer
              </h4>
              <p className="text-sm text-secondary leading-relaxed" data-id="element-805">
                This assessment is designed for general informational purposes
                under Canadian law. It does not constitute legal advice and
                should not be relied upon as a substitute for consultation with
                a qualified lawyer licensed in your province. Your answers are
                secure and confidential.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6 mb-10" data-id="element-806">
          <h3 className="text-lg font-semibold text-primary border-b border-borderColor pb-2" data-id="element-807">
            Let's get some basic context to tailor your questions
          </h3>

          <div data-id="element-808">
            <label className="block text-sm font-medium text-primary mb-2" data-id="element-809">
              Which province or territory are you located in?
            </label>
            <select className={`w-full border border-borderColor rounded-lg px-4 py-3 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-opacity-20 focus:border-opacity-100 bg-white ${assessmentType === 'personal' ? 'focus:ring-violetBlue focus:border-violetBlue' : 'focus:ring-turquoise focus:border-turquoise'}`} value={setupData.province} onChange={e => setSetupData({
            ...setupData,
            province: e.target.value
          })} data-id="element-810">
              <option value="Ontario" data-id="element-811">Ontario</option>
              <option value="British Columbia" data-id="element-812">British Columbia</option>
              <option value="Alberta" data-id="element-813">Alberta</option>
              <option value="Quebec" data-id="element-814">Quebec</option>
              <option value="Manitoba" data-id="element-815">Manitoba</option>
              <option value="Saskatchewan" data-id="element-816">Saskatchewan</option>
              <option value="Nova Scotia" data-id="element-817">Nova Scotia</option>
              <option value="New Brunswick" data-id="element-818">New Brunswick</option>
              <option value="Newfoundland and Labrador" data-id="element-819">
                Newfoundland and Labrador
              </option>
              <option value="Prince Edward Island" data-id="element-820">Prince Edward Island</option>
              <option value="Northwest Territories" data-id="element-821">
                Northwest Territories
              </option>
              <option value="Nunavut" data-id="element-822">Nunavut</option>
              <option value="Yukon" data-id="element-823">Yukon</option>
            </select>
          </div>

          {assessmentType === 'personal' ? <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-824">
                <div data-id="element-825">
                  <label className="block text-sm font-medium text-primary mb-3" data-id="element-826">
                    Relationship Status
                  </label>
                  <select className="w-full border border-borderColor rounded-lg px-4 py-3 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-violetBlue/20 focus:border-violetBlue bg-white" value={setupData.relationship} onChange={e => setSetupData({
                ...setupData,
                relationship: e.target.value
              })} data-id="element-827">
                    <option value="single" data-id="element-828">Single</option>
                    <option value="married" data-id="element-829">Married</option>
                    <option value="common-law" data-id="element-830">Common-law</option>
                    <option value="separated" data-id="element-831">Separated</option>
                    <option value="divorced" data-id="element-832">Divorced</option>
                    <option value="widowed" data-id="element-833">Widowed</option>
                  </select>
                </div>
                <div data-id="element-834">
                  <label className="block text-sm font-medium text-primary mb-3" data-id="element-835">
                    Do you have children?
                  </label>
                  <div className="flex gap-4" data-id="element-836">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-837">
                      <input type="radio" name="children" value="yes" checked={setupData.children === 'yes'} onChange={e => setSetupData({
                    ...setupData,
                    children: e.target.value
                  })} className="w-4 h-4 text-violetBlue focus:ring-violetBlue" data-id="element-838" />
                      <span className="text-sm text-primary" data-id="element-839">Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-840">
                      <input type="radio" name="children" value="no" checked={setupData.children === 'no'} onChange={e => setSetupData({
                    ...setupData,
                    children: e.target.value
                  })} className="w-4 h-4 text-violetBlue focus:ring-violetBlue" data-id="element-841" />
                      <span className="text-sm text-primary" data-id="element-842">No</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-843">
                <div data-id="element-844">
                  <label className="block text-sm font-medium text-primary mb-3" data-id="element-845">
                    Do you own property?
                  </label>
                  <div className="flex gap-4" data-id="element-846">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-847">
                      <input type="radio" name="property" value="yes" checked={setupData.property === 'yes'} onChange={e => setSetupData({
                    ...setupData,
                    property: e.target.value
                  })} className="w-4 h-4 text-violetBlue focus:ring-violetBlue" data-id="element-848" />
                      <span className="text-sm text-primary" data-id="element-849">Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-850">
                      <input type="radio" name="property" value="no" checked={setupData.property === 'no'} onChange={e => setSetupData({
                    ...setupData,
                    property: e.target.value
                  })} className="w-4 h-4 text-violetBlue focus:ring-violetBlue" data-id="element-851" />
                      <span className="text-sm text-primary" data-id="element-852">No</span>
                    </label>
                  </div>
                </div>
                <div data-id="element-853">
                  <label className="block text-sm font-medium text-primary mb-3" data-id="element-854">
                    Are you a landlord?
                  </label>
                  <div className="flex gap-4" data-id="element-855">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-856">
                      <input type="radio" name="landlord" value="yes" checked={setupData.landlord === 'yes'} onChange={e => setSetupData({
                    ...setupData,
                    landlord: e.target.value
                  })} className="w-4 h-4 text-violetBlue focus:ring-violetBlue" data-id="element-857" />
                      <span className="text-sm text-primary" data-id="element-858">Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-859">
                      <input type="radio" name="landlord" value="no" checked={setupData.landlord === 'no'} onChange={e => setSetupData({
                    ...setupData,
                    landlord: e.target.value
                  })} className="w-4 h-4 text-violetBlue focus:ring-violetBlue" data-id="element-860" />
                      <span className="text-sm text-primary" data-id="element-861">No</span>
                    </label>
                  </div>
                </div>
              </div>

              <div data-id="element-862">
                <label className="block text-sm font-medium text-primary mb-3" data-id="element-863">
                  Are you planning to leave Canada or sponsor someone to come to
                  Canada?
                </label>
                <div className="flex gap-4" data-id="element-864">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-865">
                    <input type="radio" name="leavingCanada" value="yes" checked={setupData.leavingCanada === 'yes'} onChange={e => setSetupData({
                  ...setupData,
                  leavingCanada: e.target.value
                })} className="w-4 h-4 text-violetBlue focus:ring-violetBlue" data-id="element-866" />
                    <span className="text-sm text-primary" data-id="element-867">Yes</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-868">
                    <input type="radio" name="leavingCanada" value="no" checked={setupData.leavingCanada === 'no'} onChange={e => setSetupData({
                  ...setupData,
                  leavingCanada: e.target.value
                })} className="w-4 h-4 text-violetBlue focus:ring-violetBlue" data-id="element-869" />
                    <span className="text-sm text-primary" data-id="element-870">No</span>
                  </label>
                </div>
              </div>
            </> : <>
              <div data-id="element-871">
                <label className="block text-sm font-medium text-primary mb-2" data-id="element-872">
                  Business Name
                </label>
                <input type="text" value={setupData.businessName} onChange={e => setSetupData({
              ...setupData,
              businessName: e.target.value
            })} placeholder="Enter your business name" className="w-full border border-borderColor rounded-lg px-4 py-3 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise" data-id="element-873" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-874">
                <div data-id="element-875">
                  <label className="block text-sm font-medium text-primary mb-2" data-id="element-876">
                    Business Type
                  </label>
                  <select value={setupData.businessType} onChange={e => setSetupData({
                ...setupData,
                businessType: e.target.value
              })} className="w-full border border-borderColor rounded-lg px-4 py-3 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise bg-white" data-id="element-877">
                    <option data-id="element-878">Corporation</option>
                    <option data-id="element-879">Sole Proprietorship</option>
                    <option data-id="element-880">Partnership</option>
                    <option data-id="element-881">Other</option>
                  </select>
                </div>
                <div data-id="element-882">
                  <label className="block text-sm font-medium text-primary mb-2" data-id="element-883">
                    Number of Employees
                  </label>
                  <select value={setupData.employees} onChange={e => setSetupData({
                ...setupData,
                employees: e.target.value
              })} className="w-full border border-borderColor rounded-lg px-4 py-3 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-turquoise/20 focus:border-turquoise bg-white" data-id="element-884">
                    <option data-id="element-885">Just me (0)</option>
                    <option data-id="element-886">1-10</option>
                    <option data-id="element-887">11-50</option>
                    <option data-id="element-888">51+</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-889">
                <div data-id="element-890">
                  <label className="block text-sm font-medium text-primary mb-3" data-id="element-891">
                    Does your business have a website?
                  </label>
                  <div className="flex gap-4" data-id="element-892">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-893">
                      <input type="radio" name="hasWebsite" value="yes" checked={setupData.hasWebsite === 'yes'} onChange={e => setSetupData({
                    ...setupData,
                    hasWebsite: e.target.value
                  })} className="w-4 h-4 text-turquoise focus:ring-turquoise" data-id="element-894" />
                      <span className="text-sm text-primary" data-id="element-895">Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-896">
                      <input type="radio" name="hasWebsite" value="no" checked={setupData.hasWebsite === 'no'} onChange={e => setSetupData({
                    ...setupData,
                    hasWebsite: e.target.value
                  })} className="w-4 h-4 text-turquoise focus:ring-turquoise" data-id="element-897" />
                      <span className="text-sm text-primary" data-id="element-898">No</span>
                    </label>
                  </div>
                </div>
                <div data-id="element-899">
                  <label className="block text-sm font-medium text-primary mb-3" data-id="element-900">
                    Do you create original IP or brands?
                  </label>
                  <div className="flex gap-4" data-id="element-901">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-902">
                      <input type="radio" name="createsIP" value="yes" checked={setupData.createsIP === 'yes'} onChange={e => setSetupData({
                    ...setupData,
                    createsIP: e.target.value
                  })} className="w-4 h-4 text-turquoise focus:ring-turquoise" data-id="element-903" />
                      <span className="text-sm text-primary" data-id="element-904">Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-905">
                      <input type="radio" name="createsIP" value="no" checked={setupData.createsIP === 'no'} onChange={e => setSetupData({
                    ...setupData,
                    createsIP: e.target.value
                  })} className="w-4 h-4 text-turquoise focus:ring-turquoise" data-id="element-906" />
                      <span className="text-sm text-primary" data-id="element-907">No</span>
                    </label>
                  </div>
                </div>
              </div>
            </>}
        </div>

        <div className="flex justify-end pt-6 border-t border-borderColor" data-id="element-908">
          <button onClick={startWizard} className={`${themeBg} ${themeHoverBg} text-white font-medium py-3 px-8 rounded-xl transition-colors flex items-center gap-2`} data-id="element-909">
            Begin Assessment
            <Play className="w-4 h-4 fill-current" data-id="element-910" />
          </button>
        </div>
      </div>
    </div>;
  const renderWizard = () => {
    const sections = assessmentType === 'personal' ? PERSONAL_SECTIONS : BUSINESS_SECTIONS;
    const allQuestions = assessmentType === 'personal' ? PERSONAL_QUESTIONS : BUSINESS_QUESTIONS;
    const filteredQuestions = filterQuestions(allQuestions, setupData, assessmentType);
    const currentSection = sections[currentSectionIndex];
    const currentQuestions = filteredQuestions[currentSectionIndex];
    const progress = Math.round(currentSectionIndex / sections.length * 100);
    const isSectionComplete = currentQuestions.every(q => answers[q.id] !== undefined);
    return <div className="flex flex-col h-[calc(100vh-64px)] bg-gray-50" data-id="element-911">
        {/* Wizard Header / Progress */}
        <div className="bg-white border-b border-borderColor px-4 sm:px-6 py-4 flex items-center justify-between z-10" data-id="element-912">
          <div className="flex items-center gap-4" data-id="element-913">
            <button onClick={() => setViewState('setup')} className="p-2 hover:bg-aliceBlue rounded-lg transition-colors text-secondary" data-id="element-914">
              <ArrowLeft className="w-5 h-5" data-id="element-915" />
            </button>
            <div data-id="element-916">
              <h2 className="text-sm font-bold text-primary hidden sm:block" data-id="element-917">
                {assessmentType === 'personal' ? 'Personal' : 'Business'} Legal
                Health Check
              </h2>
              <div className="flex items-center gap-2 mt-1" data-id="element-918">
                <div className="w-32 sm:w-48 h-2 bg-borderColor rounded-full overflow-hidden" data-id="element-919">
                  <motion.div className={`h-full ${themeBg}`} initial={{
                  width: 0
                }} animate={{
                  width: `${progress}%`
                }} transition={{
                  duration: 0.5
                }} data-id="element-920" />
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${themeLightBg} ${themeText}`} data-id="element-921">
                  {progress}%
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4" data-id="element-922">
            <div className="hidden sm:flex items-center gap-2 text-sm font-medium text-secondary" data-id="element-923">
              {isSaving ? <>
                  <Loader2 className={`w-4 h-4 animate-spin ${themeText}`} data-id="element-924" />
                  <span className={themeText} data-id="element-925">Saving...</span>
                </> : <>
                  <CheckCircle className="w-4 h-4 text-green-500" data-id="element-926" />
                  <span data-id="element-927">Saved</span>
                </>}
            </div>
            <button onClick={saveDraft} disabled={isSaving} className="hidden sm:flex items-center gap-2 text-sm font-medium text-secondary hover:text-primary transition-colors disabled:opacity-50" data-id="element-928">
              <Save className="w-4 h-4" data-id="element-929" />
              Save Draft
            </button>
            <div className="w-px h-4 bg-borderColor hidden sm:block" data-id="element-930"></div>
            <button onClick={() => setViewState('landing')} className="text-sm font-medium text-secondary hover:text-primary transition-colors" data-id="element-931">
              Exit
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex" data-id="element-932">
          {/* Left Sidebar - Navigation */}
          <div className="hidden lg:block w-64 bg-white border-r border-borderColor overflow-y-auto" data-id="element-933">
            <div className="p-6" data-id="element-934">
              <h3 className="text-xs font-bold text-secondary uppercase tracking-wider mb-4" data-id="element-935">
                Sections
              </h3>
              <div className="space-y-1" data-id="element-936">
                {sections.map((sec, idx) => {
                const isCompleted = idx < currentSectionIndex;
                const isCurrent = idx === currentSectionIndex;
                const Icon = sec.icon;
                return <div key={sec.id} className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${isCurrent ? `${themeLightBg} border ${themeBorderLight}` : isCompleted ? 'hover:bg-gray-50 cursor-pointer' : 'opacity-50'}`} onClick={() => {
                  if (isCompleted) {
                    setDirection(idx > currentSectionIndex ? 1 : -1);
                    setCurrentSectionIndex(idx);
                  }
                }} data-id="element-937">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${isCompleted ? 'bg-green-500 text-white' : isCurrent ? `${themeBg} text-white` : 'bg-borderColor text-secondary'}`} data-id="element-938">
                        {isCompleted ? <motion.div initial={{
                      scale: 0
                    }} animate={{
                      scale: 1
                    }} transition={{
                      type: 'spring',
                      stiffness: 300,
                      damping: 20
                    }} data-id="element-939">
                            <Check className="w-4 h-4" data-id="element-940" />
                          </motion.div> : <Icon className="w-4 h-4" data-id="element-941" />}
                      </div>
                      <span className={`text-sm font-medium ${isCurrent ? themeText : 'text-primary'}`} data-id="element-942">
                        {sec.title}
                      </span>
                    </div>;
              })}
              </div>
            </div>
          </div>

          {/* Center - Questions */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-8 lg:p-12 overflow-x-hidden" data-id="element-943">
            <div className="max-w-2xl mx-auto" data-id="element-944">
              <AnimatePresence mode="wait" custom={direction} data-id="element-945">
                <motion.div key={currentSectionIndex} custom={direction} initial={d => ({
                opacity: 0,
                x: d * 60
              })} animate={{
                opacity: 1,
                x: 0
              }} exit={d => ({
                opacity: 0,
                x: d * -60
              })} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} data-id="element-946">
                  <div className="mb-8" data-id="element-947">
                    <div className={`inline-flex items-center gap-2 px-3 py-1 ${themeLightBg} ${themeText} text-xs font-bold rounded-full mb-4 uppercase tracking-wide`} data-id="element-948">
                      Section {currentSectionIndex + 1} of {sections.length}
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-primary mb-2" data-id="element-949">
                      {currentSection.title}
                    </h2>
                    <p className="text-secondary" data-id="element-950">
                      Answer a few questions to evaluate your readiness in this
                      area.
                    </p>
                  </div>

                  <div className="space-y-6 mb-12" data-id="element-951">
                    {currentQuestions.map((q, qIdx) => <div key={q.id} className="bg-white border border-borderColor rounded-xl p-6 shadow-sm" data-id="element-952">
                        <h4 className="text-base font-semibold text-primary mb-2 leading-relaxed" data-id="element-953">
                          {q.text}
                        </h4>
                        <p className="text-xs text-secondary mb-4 leading-relaxed italic" data-id="element-954">
                          {q.context}
                        </p>
                        <div className="space-y-3" data-id="element-955">
                          {q.options.map((opt, optIdx) => {
                        const isSelected = answers[q.id] === opt;
                        return <label key={optIdx} className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${isSelected ? `${themeBorder} ${themeLightBg}` : 'border-borderColor hover:bg-aliceBlue'}`} data-id="element-956">
                                <input type="radio" name={q.id} value={opt} checked={isSelected} onChange={() => handleAnswer(q.id, opt)} className={`w-4 h-4 ${themeText} focus:ring-opacity-50 ${assessmentType === 'personal' ? 'focus:ring-violetBlue' : 'focus:ring-turquoise'}`} data-id="element-957" />
                                <span className="ml-3 text-sm font-medium text-primary" data-id="element-958">
                                  {opt}
                                </span>
                              </label>;
                      })}
                        </div>
                      </div>)}
                    {currentQuestions.length === 0 && <div className="bg-white border border-borderColor rounded-xl p-8 text-center shadow-sm" data-id="element-959">
                        <CheckCircle className={`w-12 h-12 mx-auto mb-4 ${themeText}`} data-id="element-960" />
                        <h4 className="text-lg font-bold text-primary mb-2" data-id="element-961">
                          No questions needed
                        </h4>
                        <p className="text-secondary" data-id="element-962">
                          Based on your setup answers, this section is not
                          applicable to your situation.
                        </p>
                      </div>}
                  </div>

                  <div className="flex items-center justify-between pt-6 border-t border-borderColor" data-id="element-963">
                    <button onClick={prevSection} className="px-6 py-3 text-sm font-medium text-secondary hover:text-primary transition-colors" data-id="element-964">
                      Back
                    </button>
                    <button onClick={nextSection} disabled={!isSectionComplete} className={`${isSectionComplete ? `${themeBg} ${themeHoverBg} text-white` : 'bg-disabled text-white cursor-not-allowed'} font-medium py-3 px-8 rounded-xl transition-colors flex items-center gap-2 shadow-sm`} data-id="element-965">
                      {currentSectionIndex === sections.length - 1 ? 'Complete Assessment' : 'Continue'}
                      {currentSectionIndex !== sections.length - 1 && <ChevronRight className="w-4 h-4" data-id="element-966" />}
                    </button>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Right Sidebar - Helper */}
          <div className="hidden xl:block w-80 bg-white border-l border-borderColor p-6 overflow-y-auto" data-id="element-967">
            <div className={`${themeLightBg} rounded-xl p-5 mb-6`} data-id="element-968">
              <div className="flex items-center gap-2 mb-3" data-id="element-969">
                <Info className={`w-5 h-5 ${themeText}`} data-id="element-970" />
                <h4 className="font-bold text-primary" data-id="element-971">Why this matters</h4>
              </div>
              <p className="text-sm text-secondary leading-relaxed" data-id="element-972">
                {currentSection.whyItMatters}
              </p>
            </div>

            <div className="bg-amber-50 rounded-xl p-5 mb-6 border border-amber-100" data-id="element-973">
              <div className="flex items-center gap-2 mb-3" data-id="element-974">
                <FileWarning className="w-5 h-5 text-amber-600" data-id="element-975" />
                <h4 className="font-bold text-primary" data-id="element-976">Commonly overlooked</h4>
              </div>
              <p className="text-sm text-secondary leading-relaxed" data-id="element-977">
                {currentSection.commonlyOverlooked}
              </p>
            </div>

            <div className="bg-blue-50 rounded-xl p-5 mb-6 border border-blue-100" data-id="element-978">
              <div className="flex items-center gap-2 mb-3" data-id="element-979">
                <RefreshCw className="w-5 h-5 text-blue-600" data-id="element-980" />
                <h4 className="font-bold text-primary" data-id="element-981">When to review</h4>
              </div>
              <p className="text-sm text-secondary leading-relaxed" data-id="element-982">
                {currentSection.whenToReview}
              </p>
            </div>

            <div className="border border-borderColor rounded-xl p-5" data-id="element-983">
              <h4 className="font-bold text-primary mb-4" data-id="element-984">Your Progress</h4>
              <div className="space-y-4" data-id="element-985">
                <div className="flex items-center justify-between text-sm" data-id="element-986">
                  <span className="text-secondary" data-id="element-987">Sections Completed</span>
                  <span className="font-medium text-primary" data-id="element-988">
                    {currentSectionIndex} / {sections.length}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm" data-id="element-989">
                  <span className="text-secondary" data-id="element-990">Est. Time Remaining</span>
                  <span className="font-medium text-primary" data-id="element-991">
                    {Math.max(1, (sections.length - currentSectionIndex) * 2)}{' '}
                    mins
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Save Toast */}
        <AnimatePresence data-id="element-992">
          {showSaveToast && <motion.div initial={{
          opacity: 0,
          y: 50
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 50
        }} className="fixed bottom-6 right-6 bg-gray-900 text-white px-4 py-3 rounded-lg shadow-xl flex items-center gap-3 z-50" data-id="element-993">
              <CheckCircle className="w-5 h-5 text-green-400" data-id="element-994" />
              <span className="text-sm font-medium" data-id="element-995">
                Draft saved successfully
              </span>
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  const renderResults = () => {
    const sections = assessmentType === 'personal' ? PERSONAL_SECTIONS : BUSINESS_SECTIONS;
    // Find weakest categories for dynamic summary
    const weakestCategories = sections.map((sec, idx) => ({
      name: sec.title,
      id: sec.id,
      score: sectionScores[idx] || 0
    })).sort((a, b) => a.score - b.score).slice(0, 2);
    const summaryText = weakestCategories.length > 0 && weakestCategories[0].score < 70 ? `Your weakest areas are ${weakestCategories[0].name}${weakestCategories.length > 1 ? ` and ${weakestCategories[1].name}` : ''} — addressing these gaps should be your top priority.` : 'You have a solid foundation across all categories.';
    return <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-id="element-996">
        <div className="flex items-center justify-between mb-8" data-id="element-997">
          <button onClick={handleBackToOverview} className="flex items-center gap-2 text-secondary hover:text-primary transition-colors text-sm font-medium" data-id="element-998">
            <ArrowLeft className="w-4 h-4" data-id="element-999" />
            {onExit ? 'Back to Dashboard' : 'Back to Overview'}
          </button>
          <div className="flex items-center gap-3" data-id="element-1000">
            <button onClick={() => setShowDownloadModal(true)} className="flex items-center gap-2 px-4 py-2 bg-white border border-borderColor rounded-lg text-sm font-medium text-secondary hover:text-primary hover:bg-aliceBlue transition-colors shadow-sm" data-id="element-1001">
              <Download className="w-4 h-4" data-id="element-1002" />
              Download PDF
            </button>
            <button onClick={() => setShowShareModal(true)} className="flex items-center gap-2 px-4 py-2 bg-white border border-borderColor rounded-lg text-sm font-medium text-secondary hover:text-primary hover:bg-aliceBlue transition-colors shadow-sm" data-id="element-1003">
              <Share2 className="w-4 h-4" data-id="element-1004" />
              Share
            </button>
          </div>
        </div>

        {/* Summary Header Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-id="element-1005">
          <div className="bg-white border border-borderColor rounded-xl p-4 shadow-sm" data-id="element-1006">
            <p className="text-xs text-secondary font-medium uppercase tracking-wider mb-1" data-id="element-1007">
              Assessment Type
            </p>
            <p className="text-sm font-bold text-primary capitalize" data-id="element-1008">
              {assessmentType} Legal Health
            </p>
          </div>
          <div className="bg-white border border-borderColor rounded-xl p-4 shadow-sm" data-id="element-1009">
            <p className="text-xs text-secondary font-medium uppercase tracking-wider mb-1" data-id="element-1010">
              Date Completed
            </p>
            <p className="text-sm font-bold text-primary" data-id="element-1011">Today</p>
          </div>
          <div className="bg-white border border-borderColor rounded-xl p-4 shadow-sm" data-id="element-1012">
            <p className="text-xs text-secondary font-medium uppercase tracking-wider mb-1" data-id="element-1013">
              Issues Found
            </p>
            <p className="text-sm font-bold text-primary" data-id="element-1014">
              {actionPlanItems.length} Action Items
            </p>
          </div>
          <div className="bg-white border border-borderColor rounded-xl p-4 shadow-sm" data-id="element-1015">
            <p className="text-xs text-secondary font-medium uppercase tracking-wider mb-1" data-id="element-1016">
              Risk Level
            </p>
            <p className={`text-sm font-bold ${score >= 70 ? 'text-green-600' : score >= 40 ? 'text-amber-600' : 'text-destructive'}`} data-id="element-1017">
              {score >= 70 ? 'Low Risk' : score >= 40 ? 'Medium Risk' : 'High Risk'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8" data-id="element-1018">
          {/* Main Score Card */}
          <div className="lg:col-span-2 bg-white border border-borderColor rounded-2xl p-8 shadow-sm relative overflow-hidden" data-id="element-1019">
            <div className={`absolute top-0 right-0 w-64 h-64 ${score >= 70 ? 'bg-green-50' : score >= 40 ? 'bg-amber-50' : 'bg-red-50'} rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none`} data-id="element-1020"></div>

            <div className="relative z-10 flex flex-col sm:flex-row items-center sm:items-start gap-8" data-id="element-1021">
              <div className="relative flex-shrink-0" data-id="element-1022">
                <svg className="w-40 h-40 transform -rotate-90" data-id="element-1023">
                  <circle cx="80" cy="80" r="70" stroke="currentColor" strokeWidth="12" fill="transparent" className="text-borderColor" data-id="element-1024" />
                  <motion.circle cx="80" cy="80" r="70" stroke="currentColor" strokeWidth="12" fill="transparent" strokeDasharray={440} initial={{
                  strokeDashoffset: 440
                }} animate={{
                  strokeDashoffset: 440 - 440 * score / 100
                }} transition={{
                  duration: 1.5,
                  ease: 'easeOut'
                }} className={score >= 70 ? 'text-green-500' : score >= 40 ? 'text-amber-500' : 'text-destructive'} data-id="element-1025" />
                </svg>
                <AnimatedScore score={score} data-id="element-1026" />
              </div>

              <div className="text-center sm:text-left flex-1" data-id="element-1027">
                <div className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full mb-3 uppercase tracking-wide ${score >= 70 ? 'bg-green-100 text-green-800' : score >= 40 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`} data-id="element-1028">
                  <AlertCircle className="w-3.5 h-3.5" data-id="element-1029" />
                  {score >= 70 ? 'Strong Foundation' : score >= 40 ? 'Needs Review' : 'Action Recommended'}
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-3" data-id="element-1030">
                  Your Legal Health Score
                </h1>
                <p className="text-secondary leading-relaxed mb-6" data-id="element-1031">
                  {summaryText}
                </p>
                <button onClick={() => setViewState('action-plan')} className={`${themeBg} ${themeHoverBg} text-white font-medium py-3 px-6 rounded-xl transition-colors shadow-sm w-full sm:w-auto`} data-id="element-1032">
                  View Personalized Action Plan
                </button>
              </div>
            </div>
          </div>

          {/* Priority Issues */}
          <div className="bg-white border border-borderColor rounded-2xl p-6 shadow-sm flex flex-col" data-id="element-1033">
            <h3 className="text-lg font-bold text-primary mb-4 flex items-center gap-2" data-id="element-1034">
              <AlertTriangle className={`w-5 h-5 ${score >= 70 ? 'text-green-500' : score >= 40 ? 'text-amber-500' : 'text-destructive'}`} data-id="element-1035" />
              Top Priority Issues
            </h3>
            <div className="space-y-4 flex-grow" data-id="element-1036">
              {actionPlanItems.filter(item => item.priority === 'high').length > 0 ? actionPlanItems.filter(item => item.priority === 'high').slice(0, 3).map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-1037">
                      <div className="w-2 h-2 rounded-full bg-destructive mt-2 flex-shrink-0" data-id="element-1038"></div>
                      <p className="text-sm text-primary font-medium" data-id="element-1039">
                        {item.text}
                      </p>
                    </div>) : actionPlanItems.filter(item => item.priority === 'medium').length > 0 ? actionPlanItems.filter(item => item.priority === 'medium').slice(0, 3).map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-1040">
                      <div className="w-2 h-2 rounded-full bg-amber-500 mt-2 flex-shrink-0" data-id="element-1041"></div>
                      <p className="text-sm text-primary font-medium" data-id="element-1042">
                        {item.text}
                      </p>
                    </div>) : <div className="flex items-center gap-3 text-green-600" data-id="element-1043">
                  <CheckCircle className="w-5 h-5" data-id="element-1044" />
                  <p className="text-sm font-medium" data-id="element-1045">
                    No critical issues found.
                  </p>
                </div>}
            </div>
            <button onClick={() => navigate('/legal-help')} className={`w-full mt-6 bg-white border-2 ${themeBorder} ${themeText} ${themeLightBg} font-medium py-2.5 px-4 rounded-lg transition-colors text-sm`} data-id="element-1046">
              Book Legal Advice Call
            </button>
          </div>
        </div>

        {/* Category Breakdown */}
        <h3 className="text-xl font-bold text-primary mb-6" data-id="element-1047">
          Category Breakdown
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-id="element-1048">
          {sections.map((sec, idx) => {
          const Icon = sec.icon;
          const catScore = sectionScores[idx] || 0;
          const statusColor = catScore >= 70 ? 'text-green-500 bg-green-100' : catScore >= 40 ? 'text-amber-500 bg-amber-100' : 'text-destructive bg-red-100';
          const summary = getSectionSummary(sec.id, answers, assessmentType, catScore);
          return <div key={sec.id} className="bg-white border border-borderColor rounded-xl p-5 shadow-sm" data-id="element-1049">
                <div className="flex items-center justify-between mb-4" data-id="element-1050">
                  <div className="flex items-center gap-3" data-id="element-1051">
                    <div className={`p-2 rounded-lg ${statusColor}`} data-id="element-1052">
                      <Icon className="w-5 h-5" data-id="element-1053" />
                    </div>
                    <h4 className="font-semibold text-primary" data-id="element-1054">{sec.title}</h4>
                  </div>
                  <span className="text-lg font-bold text-primary" data-id="element-1055">
                    {catScore}%
                  </span>
                </div>
                <div className="w-full bg-borderColor rounded-full h-2 mb-3" data-id="element-1056">
                  <div className={`h-2 rounded-full ${catScore >= 70 ? 'bg-green-500' : catScore >= 40 ? 'bg-amber-500' : 'bg-destructive'}`} style={{
                width: `${catScore}%`
              }} data-id="element-1057"></div>
                </div>
                <p className="text-xs text-secondary leading-relaxed" data-id="element-1058">
                  {summary}
                </p>
              </div>;
        })}
        </div>

        {/* Contextual Recommendations instead of Upsell Banner */}
        <h3 className="text-xl font-bold text-primary mb-6" data-id="element-1059">
          Recommended Next Steps
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1060">
          <div className="bg-white border border-borderColor rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-1061">
            <div className="p-3 bg-aliceBlue rounded-lg w-fit mb-4" data-id="element-1062">
              <FileText className="w-6 h-6 text-violetBlue" data-id="element-1063" />
            </div>
            <h4 className="font-bold text-primary mb-2" data-id="element-1064">
              Create Missing Documents
            </h4>
            <p className="text-sm text-secondary mb-4" data-id="element-1065">
              You have missing legal protections. Use our template library to
              draft what you need.
            </p>
            <button onClick={() => navigate('/all-templates')} className="text-sm font-medium text-violetBlue hover:underline" data-id="element-1066">
              Browse Templates &rarr;
            </button>
          </div>
          <div className="bg-white border border-borderColor rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-1067">
            <div className="p-3 bg-turquoise/10 rounded-lg w-fit mb-4" data-id="element-1068">
              <HelpCircle className="w-6 h-6 text-turquoise" data-id="element-1069" />
            </div>
            <h4 className="font-bold text-primary mb-2" data-id="element-1070">Ask LawVo AI</h4>
            <p className="text-sm text-secondary mb-4" data-id="element-1071">
              Have specific questions about your results? Our AI Copilot can
              provide immediate guidance.
            </p>
            <button onClick={() => navigate('/ai-copilot')} className="text-sm font-medium text-turquoise hover:underline" data-id="element-1072">
              Open AI Copilot &rarr;
            </button>
          </div>
          <div className="bg-white border border-borderColor rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-1073">
            <div className="p-3 bg-red-50 rounded-lg w-fit mb-4" data-id="element-1074">
              <Shield className="w-6 h-6 text-destructive" data-id="element-1075" />
            </div>
            <h4 className="font-bold text-primary mb-2" data-id="element-1076">Talk to a Lawyer</h4>
            <p className="text-sm text-secondary mb-4" data-id="element-1077">
              Your high-priority issues require professional review. Book a
              consultation today.
            </p>
            <button onClick={() => navigate('/legal-help')} className="text-sm font-medium text-destructive hover:underline" data-id="element-1078">
              Book Consultation &rarr;
            </button>
          </div>
        </div>
      </div>;
  };
  const renderActionPlan = () => <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-id="element-1079">
      <div className="flex items-center justify-between mb-8" data-id="element-1080">
        <button onClick={() => setViewState('results')} className="flex items-center gap-2 text-secondary hover:text-primary transition-colors text-sm font-medium" data-id="element-1081">
          <ArrowLeft className="w-4 h-4" data-id="element-1082" />
          Back to Results
        </button>
        <button onClick={() => setShowDownloadModal(true)} className="flex items-center gap-2 px-4 py-2 bg-white border border-borderColor rounded-lg text-sm font-medium text-secondary hover:text-primary hover:bg-aliceBlue transition-colors shadow-sm" data-id="element-1083">
          <Download className="w-4 h-4" data-id="element-1084" />
          Export Plan
        </button>
      </div>

      <div className="mb-8" data-id="element-1085">
        <h1 className="text-3xl font-bold text-primary mb-3" data-id="element-1086">
          Your Action Plan
        </h1>
        <p className="text-secondary text-lg" data-id="element-1087">
          We've prioritized your legal tasks based on your answers. Check them
          off as you complete them to improve your Legal Health Score.
        </p>
      </div>

      <div className="space-y-6" data-id="element-1088">
        {/* High Priority */}
        {actionPlanItems.filter(item => item.priority === 'high').length > 0 && <div data-id="element-1089">
            <div className="flex items-center gap-2 mb-4" data-id="element-1090">
              <AlertTriangle className="w-5 h-5 text-destructive" data-id="element-1091" />
              <h2 className="text-xl font-bold text-primary" data-id="element-1092">
                Immediate Priorities
              </h2>
              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full ml-2" data-id="element-1093">
                Do this week
              </span>
            </div>

            <div className="bg-white border border-borderColor rounded-xl shadow-sm overflow-hidden" data-id="element-1094">
              {actionPlanItems.filter(item => item.priority === 'high').map(item => <div key={item.id} className={`p-5 border-b border-borderColor last:border-b-0 transition-colors ${item.completed ? 'bg-gray-50' : 'hover:bg-aliceBlue'}`} data-id="element-1095">
                    <div className="flex items-start gap-4" data-id="element-1096">
                      <button onClick={() => toggleActionItem(item.id)} className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${item.completed ? 'bg-green-500 border-green-500' : `border-borderColor ${themeBorder}`}`} data-id="element-1097">
                        {item.completed && <Check className="w-4 h-4 text-white" data-id="element-1098" />}
                      </button>
                      <div className="flex-1" data-id="element-1099">
                        <div className="flex items-center gap-2 mb-1" data-id="element-1100">
                          <span className={`text-xs font-semibold ${themeText} uppercase tracking-wider`} data-id="element-1101">
                            {item.category}
                          </span>
                        </div>
                        <h3 className={`text-base font-semibold mb-2 ${item.completed ? 'text-secondary line-through' : 'text-primary'}`} data-id="element-1102">
                          {item.text}
                        </h3>

                        <div className="flex items-center gap-3 mt-2 mb-3" data-id="element-1103">
                          <span className="flex items-center gap-1 text-xs text-secondary font-medium bg-gray-100 px-2 py-1 rounded-md" data-id="element-1104">
                            <Zap className="w-3 h-3" data-id="element-1105" /> Effort: {item.effort}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-secondary font-medium bg-gray-100 px-2 py-1 rounded-md" data-id="element-1106">
                            <Timer className="w-3 h-3" data-id="element-1107" /> Time: {item.time}
                          </span>
                        </div>

                        {!item.completed && <div className="flex items-center gap-3 mt-3" data-id="element-1108">
                            <button onClick={() => navigate(item.actionUrl)} className={`text-sm font-medium text-white ${themeBg} ${themeHoverBg} px-4 py-2 rounded-lg transition-colors`} data-id="element-1109">
                              {item.actionText}
                            </button>
                            <button onClick={() => navigate('/legal-help')} className="text-sm font-medium text-secondary hover:text-primary transition-colors" data-id="element-1110">
                              Need help?
                            </button>
                          </div>}
                      </div>
                    </div>
                  </div>)}
            </div>
          </div>}

        {/* Medium Priority */}
        {actionPlanItems.filter(item => item.priority === 'medium').length > 0 && <div data-id="element-1111">
            <div className="flex items-center gap-2 mb-4 mt-8" data-id="element-1112">
              <AlertCircle className="w-5 h-5 text-amber-500" data-id="element-1113" />
              <h2 className="text-xl font-bold text-primary" data-id="element-1114">
                Recommended Reviews
              </h2>
              <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-bold rounded-full ml-2" data-id="element-1115">
                Do this month
              </span>
            </div>

            <div className="bg-white border border-borderColor rounded-xl shadow-sm overflow-hidden" data-id="element-1116">
              {actionPlanItems.filter(item => item.priority === 'medium').map(item => <div key={item.id} className={`p-5 border-b border-borderColor last:border-b-0 transition-colors ${item.completed ? 'bg-gray-50' : 'hover:bg-aliceBlue'}`} data-id="element-1117">
                    <div className="flex items-start gap-4" data-id="element-1118">
                      <button onClick={() => toggleActionItem(item.id)} className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${item.completed ? 'bg-green-500 border-green-500' : `border-borderColor ${themeBorder}`}`} data-id="element-1119">
                        {item.completed && <Check className="w-4 h-4 text-white" data-id="element-1120" />}
                      </button>
                      <div className="flex-1" data-id="element-1121">
                        <div className="flex items-center gap-2 mb-1" data-id="element-1122">
                          <span className={`text-xs font-semibold ${themeText} uppercase tracking-wider`} data-id="element-1123">
                            {item.category}
                          </span>
                        </div>
                        <h3 className={`text-base font-semibold mb-2 ${item.completed ? 'text-secondary line-through' : 'text-primary'}`} data-id="element-1124">
                          {item.text}
                        </h3>

                        <div className="flex items-center gap-3 mt-2 mb-3" data-id="element-1125">
                          <span className="flex items-center gap-1 text-xs text-secondary font-medium bg-gray-100 px-2 py-1 rounded-md" data-id="element-1126">
                            <Zap className="w-3 h-3" data-id="element-1127" /> Effort: {item.effort}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-secondary font-medium bg-gray-100 px-2 py-1 rounded-md" data-id="element-1128">
                            <Timer className="w-3 h-3" data-id="element-1129" /> Time: {item.time}
                          </span>
                        </div>

                        {!item.completed && <div className="flex items-center gap-3 mt-3" data-id="element-1130">
                            <button onClick={() => navigate(item.actionUrl)} className={`text-sm font-medium ${themeText} ${themeLightBg} hover:bg-opacity-80 px-4 py-2 rounded-lg transition-colors`} data-id="element-1131">
                              {item.actionText}
                            </button>
                          </div>}
                      </div>
                    </div>
                  </div>)}
            </div>
          </div>}

        {/* Low Priority */}
        {actionPlanItems.filter(item => item.priority === 'low').length > 0 && <div data-id="element-1132">
            <div className="flex items-center gap-2 mb-4 mt-8" data-id="element-1133">
              <Info className="w-5 h-5 text-blue-500" data-id="element-1134" />
              <h2 className="text-xl font-bold text-primary" data-id="element-1135">
                Optional Improvements
              </h2>
              <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-bold rounded-full ml-2" data-id="element-1136">
                Good to have
              </span>
            </div>

            <div className="bg-white border border-borderColor rounded-xl shadow-sm overflow-hidden" data-id="element-1137">
              {actionPlanItems.filter(item => item.priority === 'low').map(item => <div key={item.id} className={`p-5 border-b border-borderColor last:border-b-0 transition-colors ${item.completed ? 'bg-gray-50' : 'hover:bg-aliceBlue'}`} data-id="element-1138">
                    <div className="flex items-start gap-4" data-id="element-1139">
                      <button onClick={() => toggleActionItem(item.id)} className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${item.completed ? 'bg-green-500 border-green-500' : `border-borderColor ${themeBorder}`}`} data-id="element-1140">
                        {item.completed && <Check className="w-4 h-4 text-white" data-id="element-1141" />}
                      </button>
                      <div className="flex-1" data-id="element-1142">
                        <div className="flex items-center gap-2 mb-1" data-id="element-1143">
                          <span className={`text-xs font-semibold ${themeText} uppercase tracking-wider`} data-id="element-1144">
                            {item.category}
                          </span>
                        </div>
                        <h3 className={`text-base font-semibold mb-2 ${item.completed ? 'text-secondary line-through' : 'text-primary'}`} data-id="element-1145">
                          {item.text}
                        </h3>

                        <div className="flex items-center gap-3 mt-2 mb-3" data-id="element-1146">
                          <span className="flex items-center gap-1 text-xs text-secondary font-medium bg-gray-100 px-2 py-1 rounded-md" data-id="element-1147">
                            <Zap className="w-3 h-3" data-id="element-1148" /> Effort: {item.effort}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-secondary font-medium bg-gray-100 px-2 py-1 rounded-md" data-id="element-1149">
                            <Timer className="w-3 h-3" data-id="element-1150" /> Time: {item.time}
                          </span>
                        </div>

                        {!item.completed && <div className="flex items-center gap-3 mt-3" data-id="element-1151">
                            <button onClick={() => navigate(item.actionUrl)} className={`text-sm font-medium ${themeText} ${themeLightBg} hover:bg-opacity-80 px-4 py-2 rounded-lg transition-colors`} data-id="element-1152">
                              {item.actionText}
                            </button>
                          </div>}
                      </div>
                    </div>
                  </div>)}
            </div>
          </div>}
      </div>
    </div>;
  const renderHistory = () => <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-id="element-1153">
      <div className="flex items-center justify-between mb-8" data-id="element-1154">
        <button onClick={handleBackToOverview} className="flex items-center gap-2 text-secondary hover:text-primary transition-colors text-sm font-medium" data-id="element-1155">
          <ArrowLeft className="w-4 h-4" data-id="element-1156" />
          {onExit ? 'Back to Dashboard' : 'Back to Overview'}
        </button>
        <button onClick={handleBackToOverview} className="flex items-center gap-2 px-4 py-2 bg-violetBlue text-white rounded-lg text-sm font-medium hover:bg-[#6844E8] transition-colors shadow-sm" data-id="element-1157">
          <Plus className="w-4 h-4" data-id="element-1158" />
          New Assessment
        </button>
      </div>

      <h1 className="text-3xl font-bold text-primary mb-6" data-id="element-1159">
        Assessment History
      </h1>

      <div className="bg-white border border-borderColor rounded-xl shadow-sm overflow-hidden" data-id="element-1160">
        <div className="overflow-x-auto" data-id="element-1161">
          <table className="w-full text-left border-collapse min-w-[800px]" data-id="element-1162">
            <thead data-id="element-1163">
              <tr className="bg-gray-50 border-b border-borderColor" data-id="element-1164">
                <th className="p-4 text-xs font-semibold text-secondary uppercase tracking-wider" data-id="element-1165">
                  Type
                </th>
                <th className="p-4 text-xs font-semibold text-secondary uppercase tracking-wider" data-id="element-1166">
                  Date
                </th>
                <th className="p-4 text-xs font-semibold text-secondary uppercase tracking-wider" data-id="element-1167">
                  Status
                </th>
                <th className="p-4 text-xs font-semibold text-secondary uppercase tracking-wider" data-id="element-1168">
                  Score / Progress
                </th>
                <th className="p-4 text-xs font-semibold text-secondary uppercase tracking-wider" data-id="element-1169">
                  Top Flagged Area
                </th>
                <th className="p-4 text-xs font-semibold text-secondary uppercase tracking-wider text-right" data-id="element-1170">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-borderColor" data-id="element-1171">
              {historyItems.map(item => <tr key={item.id} className="hover:bg-aliceBlue transition-colors" data-id="element-1172">
                  <td className="p-4" data-id="element-1173">
                    <div className="flex items-center gap-3" data-id="element-1174">
                      <div className={`p-2 rounded-lg ${item.type === 'personal' ? 'bg-aliceBlue' : 'bg-turquoise/10'}`} data-id="element-1175">
                        {item.type === 'personal' ? <Activity className="w-4 h-4 text-violetBlue" data-id="element-1176" /> : <Building2 className="w-4 h-4 text-turquoise" data-id="element-1177" />}
                      </div>
                      <span className="font-medium text-primary capitalize" data-id="element-1178">
                        {item.type} Check
                      </span>
                    </div>
                  </td>
                  <td className="p-4 text-sm text-secondary" data-id="element-1179">{item.date}</td>
                  <td className="p-4" data-id="element-1180">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-1181">
                      {item.status === 'completed' ? 'Completed' : 'Draft'}
                    </span>
                  </td>
                  <td className="p-4" data-id="element-1182">
                    <div className="flex items-center gap-2 max-w-[150px]" data-id="element-1183">
                      <div className="flex-1 bg-borderColor rounded-full h-1.5" data-id="element-1184">
                        <div className={`${item.status === 'completed' ? item.score >= 70 ? 'bg-green-500' : item.score >= 40 ? 'bg-amber-500' : 'bg-destructive' : 'bg-violetBlue'} h-1.5 rounded-full`} style={{
                      width: `${item.score}%`
                    }} data-id="element-1185"></div>
                      </div>
                      <span className="text-xs font-medium text-primary w-10" data-id="element-1186">
                        {item.status === 'completed' ? `${item.score}/100` : `${item.score}%`}
                      </span>
                    </div>
                  </td>
                  <td className="p-4 text-sm text-secondary" data-id="element-1187">{item.topFlag}</td>
                  <td className="p-4 text-right" data-id="element-1188">
                    <div className="flex items-center justify-end gap-3" data-id="element-1189">
                      <button onClick={() => {
                    setAssessmentType(item.type as AssessmentType);
                    if (item.status === 'completed') {
                      generateActionPlan();
                      setViewState('results');
                    } else {
                      startSetup(item.type as AssessmentType);
                    }
                  }} className="text-sm font-medium text-violetBlue hover:text-[#6844E8] transition-colors" data-id="element-1190">
                        {item.status === 'completed' ? 'View Report' : 'Resume'}
                      </button>
                      <div className="w-px h-4 bg-borderColor" data-id="element-1191"></div>
                      <button className="text-secondary hover:text-primary transition-colors" title="Duplicate" data-id="element-1192">
                        <Copy className="w-4 h-4" data-id="element-1193" />
                      </button>
                      <button className="text-secondary hover:text-primary transition-colors" title="Archive" data-id="element-1194">
                        <Archive className="w-4 h-4" data-id="element-1195" />
                      </button>
                    </div>
                  </td>
                </tr>)}
              {historyItems.length === 0 && <tr data-id="element-1196">
                  <td colSpan={6} className="p-8 text-center text-secondary" data-id="element-1197">
                    No assessment history found.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
  return <div className={`${embedded ? 'flex-1 bg-aliceBlue' : 'min-h-screen bg-aliceBlue flex flex-col'}`} data-id="element-1198">
      {!embedded && <DashboardHeader data-id="element-1199" />}

      {/* Main Content Area */}
      <main className={`w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 ${embedded ? '' : 'flex-1'}`} data-id="element-1200">
        {viewState === 'landing' && renderLanding()}
        {viewState === 'setup' && renderSetup()}
        {viewState === 'wizard' && renderWizard()}
        {viewState === 'results' && renderResults()}
        {viewState === 'action-plan' && renderActionPlan()}
        {viewState === 'history' && renderHistory()}
      </main>

      {/* Modals */}
      <DownloadReportModal isOpen={showDownloadModal} onClose={() => setShowDownloadModal(false)} reportData={{
      type: assessmentType,
      score,
      sectionScores,
      sections: assessmentType === 'personal' ? PERSONAL_SECTIONS : BUSINESS_SECTIONS,
      actionPlanItems,
      setupData,
      answers
    }} data-id="element-1201" />

      <ShareReportModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} reportData={{
      type: assessmentType,
      score,
      id: 'req_8f92a1'
    }} onSuccess={showToast} data-id="element-1202" />

      {/* Success Toast */}
      <AnimatePresence data-id="element-1203">
        {toastMessage && <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 50
      }} className="fixed bottom-6 right-6 bg-gray-900 text-white px-4 py-3 rounded-lg shadow-xl flex items-center gap-3 z-50" data-id="element-1204">
            <CheckCircle className="w-5 h-5 text-green-400" data-id="element-1205" />
            <span className="text-sm font-medium" data-id="element-1206">{toastMessage}</span>
          </motion.div>}
      </AnimatePresence>
    </div>;
}